import { SHOT_RATIO } from '../shotShape';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CountedLabel } from '../CountedLabel';
import { createPortal } from 'react-dom';
import { useSlot } from '../../../components/sidebarSlot';
import { ASSEMBLY_CALL_SLOT_ID } from '../assemblySlot';
import {
  AlertTriangle, ChevronDown, ChevronUp, FileText, Loader2, RotateCw, Wrench,
} from 'lucide-react';
import { apiUrl, IS_DEMO, IS_LOCKED } from '../../../apiBase';
import { PartSelector } from '../PartSelector';
import { useArrowSteps } from '../stepThrough';
import { CallBadge } from '../CallBadge';
import { CALL_INSET } from '../PromptBadge';
import { SegmentedToggle } from '../SegmentedToggle';
import { PLAY_CARD, WIDE_PLAY_CARD, nextSpeed, usePlayClock } from '../ScrubBar';
import { MODES } from '../MeshViewer';
import { type DrawMode } from '../meshDrawModes';
import { mountAssembly, type AssemblyView, type ScenePart } from '../assemblyScene';
import { ClashBar, useClash, useClashPaint } from '../ClashBar';
import { PartCoordsPanel } from '../PartCoordsPanel';
import { ViewControls, type ViewAnglesSink, type ViewState } from '../ViewControls';
import { ScenePartCards } from '../ScenePartCards';
import { AXES, asCm } from '../axisGrid';
import { MovementSummary, type PoseRead } from '../MovementSummary';
import {
  approachOf, assemblyOf, awaitsPlacing, camAt, copyId, fittingChosen, isMovingCopy, isPrep, isTool, goToSlot, prepId,
  prepIds, prepOf, PREP_BY_HOLE, PREP_SHOT_V, MOVE_SHOT_V, movementMid, prepTurns, shotAt, shotDone, shotFor, isLast, isNext, isNextPrep, lastId, lastIds, lastOf, nextId, nextIds, nextOf, nextPrepId, nextPrepIds, nextPrepOf, restOf, seatOn, setFitting,
  fittingAt, movementOf, useBook, useSlotAt, type Drive, type Fitting, type PrepOn,
} from '../assemblyStore';
import {
  CLIP_SPEED, CLIP_STEP, captionAt, clipMs, finishedClip, mirroredMesh, sceneDims, statesAt, useClips, useDrawableParts, usePreps, useShownSlots,
  useToolRide, withStateInstances, withStates,
} from '../assemblyClip';
import { ClipPlayer, READER_BRIGHTNESS, READER_FRAME, showStates } from '../ClipPlayer';
import { SHOT_BOX, useFormFactor } from '../formFactor';
import { Caption } from '../public/TutorialPlayer';
import { ReaderScale } from '../ReaderScale';
import { RingAndSpeed } from '../RingTransport';
import { CaptionText } from '../CaptionText';
import { subStepLabel } from '../stepsRun';
import {
  eulerDegFromQuat, poseAt,
  type Assembly, type Vec3,
} from '../assemblyPose';
import { fittingFor, slotPartIds, useDocTools, useFittingResolver, type Slot } from '../assemblySlots';
import { seatOnSurface } from '../assemblySnap';
import { subStepKindTone, subStepKindWords } from '../subStepKinds';

// The kind a hardware set places (see setPlacement): hardware put onto hardware, as the prepared
// hardware of a gesture is.
const PREPARE_KIND = 2 as const;
import { outputChip, partTone } from '../SubStepChips';
import { subStepOutputName } from '../stepsRun';
import { tipSection } from '../ReasoningBadge';
import { isTrueScale, madeOf, meshAtSrc, meshSrc, meshStamp, saveSubstepOutput, type Job3D, type PiecePart } from '../threeDApi';
import { builtBoxOf } from '../openaiBlenderApi';
import { changedSince, liveOf, marksNow, modelMark } from '../fittingModels';
import { isDeclined, sayDeclined, sayRuleError } from '../ruleError';
import { approachFromSet, setPlacement, setPose } from '../fromSet';
import { manualSetsKnown, useManualSets } from '../hardwareSets';
import { travelFromJoint, useJointPlacement } from '../fromJoint';
import { jobBoxMm, movementShot, shotCandidates, type HiddenFrom, type ShotCandidate, type ShotSizes } from '../movementShot';
import { useHolePlacement, type HolePlacement } from '../fromHole';
import { prepShot, prepShotAt, prepShotCandidates } from '../prepShot';
import { AllClips } from '../AllClips';
import { SignoffToggle, useSignoffs } from '../assemblySignoffs';
import { ShotCandidates, withTried, type CandidateRow, type Tried } from '../ShotCandidates';
import { sceneClosed } from '../assemblyClashRemote';
import { bumpSubstepOutputs } from '../substepOutputs';
import { useLanguage } from '../../../contexts/LanguageContext';

// PHASE 5 — THE ASSEMBLY: one part going onto another, as a movement you can scrub.
//
// The phases before it read the manual; this one is the first that says what HAPPENS. What
// it produces is not a video and not a baked animation: it is a handful of numbers — which
// two parts, which way in, how far, and whether it threads — out of which poseAt computes
// every part's pose at any moment. That is why the scrubber is the point rather than a
// convenience. Play, pause, scrub and reverse are four ways of choosing t, and none of them
// is a separate code path.
//
// Nothing here is keyframed and nothing is stored inside a .glb, for a reason that is about
// the data and not about the tooling: a mesh is shared by every instance and every manual
// that uses it, while a fitting is true of these two parts in THIS manual. A screw used in
// forty notices cannot carry forty tracks.
//
// AND NO CALL. The phases before it say what the manual SAYS — Phase 3 which parts each step
// adds, Phase 4 the order within a step and which parts each sub-step handles. What the movement
// IS is worked out by rule (fromSet, fromJoint, fromHole), and where no rule can, the page says
// why rather than asking a model to guess: it asked one, and a rail came back at a frame's foot.

// The one step this phase draws, how long it takes at 1× (clipMs), and how fast it is played.
// All three come from assemblyClip, which is where a published clip reads them too: a fitting
// signed off at one speed and played to a visitor at another is not the clip anybody
// approved. The reason for a slow default is written there.
const STEP = CLIP_STEP;

// TEXTURED OR WIREFRAME, which is now all there is. This phase used to filter out a third
// mode — "Both", the wireframe drawn over the texture — because a picture carrying both at
// once is the busiest of the three exactly where the interpenetration to be spotted is
// smallest. The argument held in Phase 2 too, so the mode is gone and the filter with it.

// HOW FAST THE STEP PLAYS, and it is not on offer for now: the picker that stood beside the
// transport is gone and this is what it was left on. Shared with the published clip — see
// STEP above.
const SPEED = CLIP_SPEED;


// A PART THIS SUB-STEP DOES NOT WANT: the unticked half of the parts row, grey where a ticked
// one is amber (`partTone`, drawn exactly as the sub-step table draws it — one part, one
// colour, both screens). Grey and not a paler amber, because "off" is not a weaker version of
// the part: it is this phase being told the analysis put something in the row that is not in
// the gesture. Hovering it shows the amber it would become.
const partChipOff = 'border-slate-300 text-slate-500 hover:border-amber-400';

// A millimetre field. Small, and there are six of them.
function MmField({ label, value, onChange, step = 1, wrong = false, max }: {
  // Absent where the field is a table cell and the column header names it — see
  // MovementSummary. A label under a heading that says the same thing is the heading twice.
  label?: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  // THE NUMBER IN IT CANNOT BE TRUE. Said on the field rather than beside it: what is wrong
  // is this value, and a message under a row of five boxes is a message about none of them.
  wrong?: boolean;
  // AS HIGH AS THE FIELD GOES. The arrows stop there and a bigger number typed in lands on
  // it — refused at the edge rather than accepted and complained about, for the one kind of
  // limit that is not a matter of taste: a value past it describes something that cannot
  // exist, so there is nothing to be gained by letting it be typed.
  max?: number;
}) {
  return (
    <label className="flex flex-col items-center gap-1">
      {label && (
        <span className="text-[11px] uppercase tracking-wide text-slate-500">
          {label}
        </span>
      )}
      <input
        type="number"
        value={value}
        step={step}
        max={max}
        onChange={(e) => {
          const asked = Number(e.target.value) || 0;
          onChange(max != null ? Math.min(asked, max) : asked);
        }}
        className={`w-20 rounded-lg border bg-transparent px-2 py-1 text-center text-sm ${
          wrong
            ? 'border-red-500 font-semibold text-red-600'
            : 'border-slate-300 text-slate-800'
        }`}
      />
    </label>
  );
}

// A NOTE ABOUT ONE SUB-STEP, said only on that one: "the third hardware is not shown" from 2-3 stayed
// on screen on 2-2 and 2-4, whose rules return before saying anything (validated, or nothing to place).
function useSlotNote(slotKey: string | undefined): [string | null, (why: string | null) => void] {
  const [note, setNote] = useState<{ key: string | undefined; why: string } | null>(null);
  const set = useCallback((why: string | null) => setNote(why == null ? null : { key: slotKey, why }), [slotKey]);
  return [note && note.key === slotKey ? note.why : null, set];
}

export function AssemblyPhase({ docId }: { docId: string }) {
  // THE PICTURE'S SHAPE, mobile or desktop (see formFactor).
  const formFactor = useFormFactor();
  const { t: tr, language } = useLanguage();
  const book = useBook(docId);
  // WHERE THE PHASE IS STANDING: a sub-step of the manual. Read before anything about the
  // fitting, because which slot is open is what decides WHOSE fitting this whole page is
  // about — a repeat of another gesture shows the leader's, and has none of its own.
  // Bar the hardware prepared in the hand that the gesture using it plays first (see foldsOf).
  const allSlots = useShownSlots(docId);
  // THE DEMO STANDS ONLY WHERE SOMEBODY SIGNED OFF. Its sub-steps are prod's, and the ones
  // nobody validated are the ones prod is still working on — a half-answered movement, or
  // an empty frame with a name over it. The counter says so: "1AA (1/3)" of three that
  // play, rather than one of six where three do nothing.
  //
  // The same test the clip builder uses, so what the demo can be pointed at is exactly
  // what the published page shows: signed off AND still answered, since validating is a
  // click and a click on a slot whose parts were later cleared leaves a tick behind on
  // nothing. A repeat is judged by its leader's fitting, which is whose gesture it shows.
  const slots = useMemo(
    () => (IS_DEMO
      ? allSlots.filter((s) => {
        const f = fittingFor(book, s);
        return f.validated && fittingChosen(f);
      })
      : allSlots),
    [allSlots, book],
  );
  const here = useSlotAt(docId);
  // THE END OF THE STRIP: the product assembled, standing still — a tile on a line of its own
  // under the steps, and the screen a published tutorial ends on (see finishedClip). What the last
  // sub-step that shows a movement leaves, so it is there once that one is signed off and not
  // before: the product with its last part missing is not the product.
  const [atEnd, setAtEnd] = useState(false);
  // AND, OFF THE SUB-STEPS TOO, THE WHOLE ASSEMBLY PLAYED THROUGH (see AllClips): `atEnd` with this.
  const [atAll, setAtAll] = useState(false);
  // And whether each of the two is signed off (see SignoffToggle).
  const signoffs = useSignoffs(docId);
  const clips = useClips(docId);
  const finished = useMemo(() => {
    const last = [...allSlots].reverse().find((s) => s.step != null);
    const clip = last && clips[subStepLabel(last.step!, last.letter)];
    return clip ? finishedClip(clip) : null;
  }, [allSlots, clips]);
  // Which sub-step is open, readable from inside a call that is already in flight — a handler
  // closes over the value it was created with, and this one has to outlive several seconds of
  // waiting to know whether its answer still belongs on screen.
  const at = Math.max(0, slots.findIndex((s) => s.key === here));
  const slot = slots[at] ?? slots[0];
  // WHOSE GESTURE THIS IS, when it is not this slot's own. Four sub-steps that slide the same
  // washer onto the same screw are one movement (see Slot.repeat); the first of them is
  // answered, and the rest show what it was answered as.
  //
  // Null on the leader itself, so this doubles as "is this page read-only": there is exactly
  // one slot per run that can be asked about, positioned and signed off, and it is the one
  // where this is null.
  const leader = slot?.repeat && slot.repeat.leader !== slot.key ? slot.repeat.leader : null;
  // EVERY SLOT THIS ONE'S GESTURE IS MADE IN, leader first — itself alone where the manual
  // makes it once. What the sign-off writes a mesh for: each of them leaves its own piece,
  // "101" here and "104" three sub-steps later, and they are the same object made four times.
  const run = useMemo(
    () => (slot?.repeat ? slots.filter((s) => s.repeat?.leader === slot.repeat?.leader) : []),
    [slots, slot?.repeat],
  );
  // THE FITTING ON SCREEN — the open slot's, or the leader's where this slot repeats one.
  //
  // Against `here` and not `slot.key`: they differ for one render on a document whose stored
  // slot no longer exists, and the effect below is what settles that. Reading the fitting off
  // the fallback slot before the store agrees would show one sub-step's numbers under
  // another's name.
  const fitting = fittingAt(book, leader ?? here);
  // AND EVERY WRITE GOES THROUGH ONE DOOR, which is shut on a repeat.
  //
  // A dozen things on this page write a fitting — the tables, a dragged part, the sign-off,
  // the suggestion that fills a blank slot, the effect that adopts the analysis's kind — and
  // a repeat must make none of them, for a reason stronger than "its controls are hidden":
  // `setFitting` writes into the slot the STORE has open, so a stray write from a page
  // standing on 2B would land on 2B and quietly fork the movement it is supposed to be
  // showing. One guard here is a guarantee; ten guards at ten call sites are an intention.
  //
  // THROUGH A REF, for the same reason the fitting itself is read through one (see `latest`):
  // the viewer's drag handlers are baked into the scene at mount and never rebuilt, so a
  // `write` that closed over `leader` would carry the answer from whichever render mounted
  // them. Two slots that repeat one gesture draw the SAME meshes, so stepping from 2B back to
  // 1AA rebuilds nothing — and the leader's parts would have gone unmovable in the hand,
  // refused by a guard that was answered on another sub-step.
  const leaderRef = useRef<string | null>(null);
  leaderRef.current = leader;
  const write = (patch: Partial<Fitting>) => {
    if (leaderRef.current) return;
    setFitting(docId, patch);
  };
  // AND A REPEAT SHOWS NOTHING UNTIL THE GESTURE HAS BEEN SIGNED OFF WHERE IT IS ANSWERED.
  //
  // The same test the sign-off itself is counted by — watched AND still answered — because
  // the animation this page would draw is the leader's, and an unvalidated one is precisely
  // the thing nobody has looked at yet. Drawing it here would put six copies of a movement on
  // screen that may be wrong, and offer no way to correct any of them.
  const waiting = !!leader && !(fitting.validated && fittingChosen(fitting));
  // THE PARTS THIS PHASE CAN WORK WITH: the manual's own parts that have a model in the Parts
  // DB, named and measured. Shared with the clips published out of this phase (see
  // assemblyClip) rather than derived again here — a part drawable in the selector and not in
  // the clip would be a fitting that cannot be published, and nothing on screen saying so.
  const { byItem, meshDims, modelOf, loaded: jobsLoaded } = useDrawableParts(docId);
  const manualSets = useManualSets(docId);
  const setsKnown = manualSetsKnown(docId);
  // HOW BIG A PART IS, for placing a contact on its faces: its MESH's box, and the drawing's
  // size only where there is no mesh to measure. A face is a rectangle cut out of these three
  // numbers, so the one that decides where two parts touch has to be the shape on screen — the
  // drawing leaves an axis out often enough that reading it here announced a measured rail as
  // having no size.
  const sizeOf = (id: number | null | undefined) =>
    (id != null ? meshDims.get(id) ?? byItem.get(id)?.dims : null) ?? null;

  // Where the scene posts the viewpoint's angles. A ref and not state: see ViewAngles — the
  // readout owns them, and the phase must not re-render sixty times a second to show them.
  const viewAngles: ViewAnglesSink = useRef<((view: ViewState) => void) | null>(null);

  const host = useRef<HTMLDivElement>(null);
  const view = useRef<AssemblyView | null>(null);
  // The corner's camera switch, to the scene (see ViewControls, drawn once the scene is ready).
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  // AT THE SPEED THE RING'S BUTTON SETS: ×0.5, ×1 or ×2 (see nextSpeed).
  const [speed, setSpeed] = useState(1);
  const [drawMode, setDrawMode] = useState<DrawMode>('textured');
  // HOW IT IS LIT IS NOT ON OFFER HERE, and studio is not a default anybody can leave: the
  // scene applies it at mount and nothing on this page changes it (see assemblyScene). The
  // mesh viewer's toggle answers a question this phase does not ask — under "even" a
  // fastener is a flat grey silhouette with no readable axis, which is the right light for
  // judging a part's colour and the wrong one for watching it go in.
  const [unscaled, setUnscaled] = useState<string[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');
  // HOW MANY TIMES THE SCENE HAS BEEN ASKED FOR AGAIN. Nought until somebody presses the
  // button in the corner of the viewport; every press past that puts a new number on the end
  // of each mesh's URL, which is the whole mechanism — a different list of parts is a
  // different scene, so the mount effect tears the old one down and builds another with a
  // fresh WebGL context. See PartViewer, which does the same for one mesh.
  const [attempt, setAttempt] = useState(0);

  // WITH ITS TOOL ON THE PART IT WORKS, where a set says so (see useToolRide).
  const withRide = useToolRide(docId);
  const withPreps = usePreps(docId);
  // WHAT IS PLAYED: the fitting, its tool where a set says it rides, and the preparations played
  // before it (see Fitting.preps).
  // And reflected, on a repeat on the other side from the sub-step it repeats (see Fitting.mirrorAll).
  const mirrorAll = !!slot?.repeat?.mirror && slot.repeat.leader !== slot.key;
  // And opening on the whole, on the very first sub-step (see Fitting.opensWide).
  const opensWide = !!slot && slot.key === allSlots.find((s) => s.step != null)?.key;
  const played = useMemo(
    () => ({ ...withPreps(slot, withRide(fitting)), ...(mirrorAll ? { mirrorAll } : {}), ...(opensWide ? { opensWide } : {}) }),
    [slot, fitting, withRide, withPreps, mirrorAll, opensWide],
  );
  // THE CAMERA IS AIMED FROM WHAT IS PLAYED, not from the stored fitting: a mirrored repeat's shot is
  // the mirror of its leader's (see camAt), and aimed from the fitting it looked at the outer face
  // of the right rail while the nuts went in behind it. Through a ref, as `latest` is (see there).
  const playedRef = useRef(played);
  playedRef.current = played;
  // AND A CANDIDATE VIEW CLICKED UNDER THE TRANSPORT, played in place of its shot's own (see
  // ShotCandidates). Not saved; dropped with the sub-step.
  const [viewTried, setViewTried] = useState<Tried | null>(null);
  const triedRef = useRef(viewTried);
  triedRef.current = viewTried;
  useEffect(() => setViewTried(null), [slot?.key]);
  const asm = useMemo(() => assemblyOf(played), [played]);
  // THE STATES THIS SUB-STEP DRAWS ITS PARTS AT, as its clip does (see ClipPart.at and .driven): the
  // pointed screws' holes not yet made, each made as its screw reaches the wood. Signed off or not.
  // Kept by what they say, so a fetch that changes nothing remounts nothing.
  const allClips = useClips(docId, true);
  const clipNow = slot?.step != null ? allClips[subStepLabel(slot.step, slot.letter)] : undefined;
  const statesSig = JSON.stringify(clipNow?.parts.map((p) => [p.instanceId, p.at, p.driven, p.pieces?.map((q) => [q.name, q.at, q.driven])]) ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clipParts = useMemo(() => clipNow?.parts ?? null, [statesSig]);
  // What is drawn: the movement with each state standing where its part stands. Measured (clashes,
  // what hides what) without them: a part as it was before its holes is not in the way of anything.
  const drawnAsm = useMemo(() => (clipParts ? withStateInstances(asm, clipParts) : asm), [asm, clipParts]);

  // HOW FAR THE PART ACTUALLY TRAVELS, and whether the thread claims more of it than exists.
  //
  // The two ends of the movement are the authority: the start and the final position are what
  // the analysis answered and what the cards write, and `travel` is the distance between them.
  // The thread is a SLICE of that distance — the last stretch, over which the fastener turns
  // instead of sliding (see assemblyOf, which subtracts one from the other) — so a thread
  // deeper than the travel is not a fitting that threads more, it is a fitting whose own two
  // ends disagree with it. assemblyOf resolves that disagreement by moving the start: the
  // approach floors at zero and the part is drawn `depth` out instead of `travel` out, which
  // silently makes the movement a different length than the one the tables print.
  //
  // So it is refused rather than absorbed. The number goes red and the clip will not play —
  // there is nothing to watch that is true.
  const overThread = fitting.pitch > 0 && fitting.depth > fitting.travel;

  // AND WHAT THAT ASSEMBLY MEASURES AS: re-run whenever the fitting or its meshes change,
  // read every frame by the bar under the sign-off. See assemblyClash.
  // NOT ON A REPEAT, which has no sign-off for a clearance reading to inform — see the bar
  // itself, which is drawn on the leader only. It is geometry over two meshes; measuring it
  // six times to show it once is the work this whole grouping exists to stop.
  //
  // KEPT BY WHAT IT WAS MEASURED ON — the movement, and which model of each part (the marks a
  // sign-off keeps): coming back to a sub-step is then a lookup. The other hand of a pair is in
  // neither, and is another measurement.
  const clashKey = useMemo(
    () => JSON.stringify([asm, marksNow(fitting, modelOf, null), !!fitting.mirrored]),
    [asm, modelOf, fitting.mirrored], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const clash = useClash(view, asm, STEP, state === 'ready' && !leader, clashKey);

  // WHETHER ANYTHING IS FASTENED HERE, which is the whole of what the family says. It used to
  // say much more — how many parts were drawn, which frame the seat was written in, whether
  // there was a tool — all of it true of the kind that moved a single part and joined it to
  // nothing, and that kind is gone (see subStepKinds). What is left is the thread: a rail held
  // against a frame bites into nothing, so it has no pitch and no depth to answer.
  //
  // Read off the FITTING and not off the slot, because a fitting always has a kind while a
  // slot's is null on a step slot and on every sub-step analysed before the word was asked
  // for. Keeping the two in step is the effect below, and it is the only writer.
  const placing = fitting.kind === 'placing';


  // The gizmo's three words, keyed on the LANGUAGE rather than on the translate function.
  // In the assembly's frame, so Z is the height and Y is the depth — the opposite pairing
  // to the mesh viewer's, whose gizmo stands in three's Y-up world. Same three words; the
  // root's quarter turn is what swaps them.
  // "=0" ON EACH OF THEM, because the gizmo no longer only points: it STANDS somewhere, and
  // where it stands is the one place all three measurements are zero. "Y (depth)" names the
  // arrow; "Y (depth=0)" names the point the arrow comes out of — which is what the panel's
  // three boxes are measured from, and the thing that was nowhere written down.
  const words = useMemo(() => ({
    // NO "=0" ANY MORE. The suffix said where the arrow came OUT of — the one point in the
    // picture where all three measurements read zero — and it was true while the arms stood at
    // the origin. They are pinned in a corner now and start nowhere in particular; the origin
    // is marked by its own black cross instead. What is left is the axis and what the arm is
    // worth, which is all a corner legend can honestly claim.
    x: tr('notice.3d.width'),
    y: tr('notice.3d.depth'),
    z: tr('notice.3d.height'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [language]);

  // WHICH PART IS SELECTED, and how far a hand has pushed it since the pointer went down.
  // Held here rather than in the scene because it is a fact about the page — the panel is
  // React's, the outline is three's, and they must agree.
  //
  // WHERE THE PART IS is NOT held here, and that is the correction: it was, read live off the
  // scene, and a live reading is the wrong number to put in a box you can type into. Two ways
  // it went wrong, both of them ending in "the button does nothing".
  //
  // It included the TRAVEL. At t = 0 a washer 40 mm out reads 55 mm up, so typing 0 there
  // does not mean "seat it at zero" — it means "seat it at minus fifty-five", which is what
  // went into the fitting and is not a number anybody typed.
  //
  // And it went STALE. Nothing reported the part's new place as the scrubber moved it, so
  // the boxes still held where it was when it was clicked. Type what you see, press the
  // button, and the difference between the two is nothing — which is exactly a button that
  // does nothing, on a panel that looks right.
  //
  // So the panel shows where the part is SEATED, worked out from the fitting itself. It is
  // the same number the fitting stores, it cannot drift from it, and it does not move while
  // the animation plays — because where a part belongs is not a fact about what time it is.
  // NO PART IS DRAGGED, as in the models phase's viewer: a drag anywhere turns the camera, and a
  // part's place is typed in its card or comes from the movement's own answer.
  const [picked, setPicked] = useState<{ instanceId: string } | null>(null);
  // A place that has just been typed must not re-frame the shot. The camera is fitted when the
  // fitting changes shape, and a typed seat changes it — the world jumping the moment you set
  // down the part you were positioning.
  const held = useRef(false);
  // WHETHER THE CAMERA IS SOMEBODY'S — see the effect that plays the shot on `t`.
  const taken = useRef(false);
  // WHICH PARTS HAVE BEEN TAKEN OUT OF THE PICTURE, by instance. A way of LOOKING and not an
  // answer about the fitting, so it lives in the page's own state and is written nowhere: a
  // washer lifted out of the way to see the hole under it is not a fact about the manual, and
  // it comes back the moment the sub-step does.
  const [hidden, setHidden] = useState<string[]>([]);
  // THE PARTS AT `tt`, and each at the state its screws have reached, with the ones hidden by hand.
  const showAt = (tt: number) => {
    const v = view.current;
    if (!v) return;
    v.show(drawnAsm, STEP, tt);
    showStates(v, clipParts ? statesAt(clipParts, drawnAsm, tt, hidden) : null);
  };
  // WHOSE RULER IS OFF. The arrows measure everything on screen as one box, so there is one
  // set of them and no such thing as one part's — every card's ruler is a vote, and they all
  // have to be on. Held as the parts voting NO, like `hidden`, so a part the viewer has never
  // heard of starts with its ruler on rather than needing to be added to a list to count.
  const [rulersOff, setRulersOff] = useState<string[]>([]);
  // Whether the views each shot weighed are unfolded (see ShotCandidates). Folded by default.
  const [viewsOpen, setViewsOpen] = useState(false);
  // AND WHOSE FACES ARE NAMED — the six plates the box carries, on their own switch now that
  // the ruler no longer drags them along with it. Held as the parts that are OFF, like its two
  // neighbours and for the same reason: a part the viewer has never heard of should arrive
  // named rather than have to be added to a list to be.
  const [facesOff, setFacesOff] = useState<string[]>([]);
  // OFF UNTIL ASKED FOR, as the part viewer opens: the mesh alone, and the marks button brings
  // the rulers, the face names and the gizmo up together.
  const [gizmoOn, setGizmoOn] = useState(false);
  // WHERE THE VIEWER IS STANDING RIGHT NOW, for the camera bar's capture button. A ref and
  // not state: it changes every frame of an orbit, and this page re-renders eleven hundred
  // lines — the readout in the corner already goes through a ref sink for that reason, and
  // this rides the same report.
  const liveView = useRef<ViewState | null>(null);
  // THE FITTING AS IT IS NOW, for the scene's callbacks. They are baked into the viewer at
  // mount and the viewer is deliberately not remounted when a number changes — the meshes
  // are megabytes — so a callback reading `fitting` directly would be adding every drag to
  // the seat as it stood when the parts finished downloading.
  const latest = useRef(fitting);
  latest.current = fitting;

  // WHERE A PART SEATS, in the assembly's frame — the panel's three numbers, out of the
  // fitting and nothing else. The fixed part stands at its own seat; the moving part is
  // written in the fixed part's frame, so its place in the assembly's is the two added.
  //
  // A placing took the first branch until recently, having no fixed part to be written
  // against. Every gesture has one now (see fittingChosen), so there is one rule.
  // A COPY of the moving part (see Fitting.copies) stands at its own seat, in the same frame.
  const copyOf = (instanceId: string) => (fitting.copies ?? []).find((_, k) => copyId(k + 1) === instanceId);
  const seatedAt = (instanceId: string): [number, number, number] => {
    const { targetSeat: base, toolSeat } = fitting;
    const seat = copyOf(instanceId)?.seat ?? fitting.seat;
    if (isTool(instanceId)) return [toolSeat[0], toolSeat[1], toolSeat[2]];
    if (instanceId === 'target') return [base[0], base[1], base[2]];
    return [base[0] + seat[0], base[1] + seat[1], base[2] + seat[2]];
  };

  // AND HOW IT IS TURNED THERE, in the assembly's frame. Simpler than the position above,
  // and for a reason worth stating: all three spins are STORED in the assembly's frame, so
  // the moving part's needs no adding to the fixed part's the way its seat does. The one
  // conversion into the fixed part's frame happens in assemblyOf and nowhere else.
  const seatedSpin = (instanceId: string): [number, number, number] => {
    const own = isTool(instanceId)
      ? fitting.toolSpin
      : instanceId === 'target' ? fitting.targetSpin : copyOf(instanceId)?.spin ?? fitting.spin;
    return [own[0], own[1], own[2]];
  };

  // A place in the assembly's frame, written as a seat on the fixed part — this slot's fixed
  // part, which is the only thing this adds to seatOn. See there for why it is not a
  // subtraction.
  const seatIn = (base: Vec3, to: Vec3): Vec3 => seatOn(base, fitting.targetSpin, to);

  // THE TWO ENDS OF THE MOVE, for the box that defines it: where the moving part stands when
  // the step opens and where it comes to rest. The same call the viewer draws from, asked at
  // t=0 and t=1 rather than at the moment — these are facts about the movement, not about the
  // scrubber, so they hold still while it runs.
  //
  // In centimetres like every other position on the page. Millimetres are what is stored and
  // what the travel field is typed in, and the two live side by side here: the travel is a
  // LENGTH, which is what a manual prints, and these are PLACES, which is what the gizmo is
  // ruled in.
  const [startPose, endPose] = useMemo(() => {
    const moved = (t0: number): PoseRead | null => {
      const pose = poseAt(asm, STEP, t0).find((p) => p.instance.id === 'moving')?.pose;
      if (!pose) return null;
      return {
        pos: pose.pos.map(asCm),
        // Rounded here and not in the summary, because the summary decides what CHANGED by
        // comparing the numbers it prints: rounding after that test would let a hundredth of
        // a degree list itself as "0 → 0".
        rot: eulerDegFromQuat(pose.rot).map((v) => Math.round(v * 100) / 100),
      };
    };
    return [moved(0), moved(1)];
  }, [asm]);

  // WHAT A TYPED END OF THE MOVEMENT MEANS. Two writers, and the difference between them is
  // which of the three facts — where it rests, where it starts, what happens between — the
  // typed number is taken to be about.
  //
  // FINAL is the part's rest, and it is the same write the card's panel makes: the seat and
  // the spin, in the assembly's frame, brought into the fixed part's where the seat is
  // measured. The movement is untouched, so the starting position slides across with it —
  // which is right, because "40 mm along +Z" is still true of a part put somewhere else.
  const writeFinal = (pos: [number, number, number], rot: [number, number, number]) => {
    held.current = true;
    setPlaying(false);
    write(restOf({ pos, rot }, fitting.targetSeat, fitting.targetSpin));
  };

  // AND STARTING IS THE MOVEMENT. The rest is untouched — it is answered on the card and by
  // the table opposite — so what a typed start can change is only the way in and the distance,
  // which are exactly what the two are: the part sits at seat + axis × travel when the step
  // opens, so a start and a finish determine that pair between them.
  //
  // A start EQUAL to the finish leaves the direction alone: there is no direction in a
  // distance of nothing, and overwriting the stored one with a guess would lose the answer
  // the moment somebody typed a travel back in.
  const writeStart = (pos: [number, number, number], rot: [number, number, number]) => {
    if (!endPose) return;
    held.current = true;
    setPlaying(false);
    // The rest as it stands, in the units this is typed in: the position the opposite table
    // is showing, and the spin the part actually seats at.
    const rest = { pos: endPose.pos as unknown as Vec3, rot: fitting.spin };
    write(approachOf({ pos, rot }, rest, fitting.targetSpin));
  };

  // WHERE EVERY PART IS AT THIS INSTANT — the same call the viewer makes to draw them, on the
  // same `t`, so the panel and the picture cannot disagree.
  //
  // THE PANEL USED TO SHOW THE SEAT and only the seat, which is where the part ENDS UP. Play
  // it and the frame swung across the screen while three numbers sat still, having already
  // decided where it was going to land. Worse for a placing, where the whole of the gesture
  // is a rotation: the part visibly turned over and the orientation read 0, 0, 0 throughout —
  // the one number that was the gesture, and it was the one that never moved.
  //
  // So the boxes read the live pose. What is TYPED into them still means the seat (see
  // onValidate), which is only coherent because committing also runs the scrubber to the end:
  // there, live and seated are the same pose, and the number you typed is the number you see.
  const placed = useMemo(() => poseAt(asm, STEP, t), [asm, t]);
  const poseOf = (instanceId: string) =>
    placed.find((p) => p.instance.id === instanceId)?.pose ?? null;

  // AND THE MOVING PART'S, FOR THE MIDDLE COLUMN OF THE BAR — where it is AT THIS INSTANT,
  // between the two ends it is read against.
  //
  // The two tables either side are the movement's definition and hold still while it plays;
  // this is the only reading on the page that moves, which is what makes the pair of them
  // legible: a number that changes tells you which of the six is the gesture, and on a
  // placing — where nothing travels and the whole movement is a turn — it is the only thing
  // on the bar that says anything is happening at all.
  //
  // Rounded exactly as the ends are, so a column can be compared with its neighbours digit
  // for digit rather than by eye.
  const nowPose = useMemo((): PoseRead | null => {
    const pose = poseOf('moving');
    if (!pose) return null;
    return {
      pos: pose.pos.map(asCm),
      rot: eulerDegFromQuat(pose.rot).map((v) => Math.round(v * 100) / 100),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed]);

  // AND THE SAME, as the card shows it. The stored seat as the fallback, for the instant before a
  // fitting has an assembly to be placed in — never nothing, which would read as a part sitting
  // at the origin.
  const shownAt = (id: string): [number, number, number] => {
    const live = poseOf(id);
    return live ? [live.pos[0], live.pos[1], live.pos[2]] : seatedAt(id);
  };

  // AND HOW IT IS TURNED AT THIS INSTANT, out of the same placement.
  const spinAt = (id: string): [number, number, number] => {
    const live = poseOf(id);
    if (!live) return seatedSpin(id);
    const [x, y, z] = eulerDegFromQuat(live.rot);
    return [x, y, z];
  };

  // AND THE WORDS THE MEASUREMENTS ARE WRITTEN WITH. The mesh viewer's own — "3D width" is
  // what a mesh actually measures, as against the size it was supposed to be — because that
  // is the only one of the two claims this phase can make: nobody printed the width of a
  // washer halfway down a screw. Same memo trick as above, and for the same reason.
  const extentWords = useMemo(() => ({
    width: tr('notice.3d.actualWidth'),
    depth: tr('notice.3d.actualDepth'),
    height: tr('notice.3d.actualHeight'),
    unknown: tr('notice.3d.unknown'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [language]);

  // AND THE NAMES OF THE SIX FACES THAT BOX HAS. Which are the ASSEMBLY frame's directions and
  // not the part's own — nothing here knows which face of a leg its maker calls the front, and
  // FaceWords in meshAnnotations says so at length. Same memo trick, same reason.
  const faceWords = useMemo(() => ({
    top: tr('notice.assembly.faceTop'),
    bottom: tr('notice.assembly.faceBottom'),
    front: tr('notice.assembly.faceFront'),
    back: tr('notice.assembly.faceBack'),
    left: tr('notice.assembly.faceLeft'),
    right: tr('notice.assembly.faceRight'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [language]);

  // WHETHER THE MANUAL'S PARTS ARE RESOLVED TO THEIR CATALOG ROWS — the slots are built on it.
  const { loaded: partsResolved } = useFittingResolver(docId);
  const tools = useDocTools(docId);
  // AND THE STORE IS TOLD WHERE THE PHASE ACTUALLY IS. `here` is a key somebody's browser
  // wrote down, and it can name a sub-step this manual no longer has — a re-analysis that
  // letters its gestures differently orphans every one of them at once. The line above already
  // falls through to the first slot so there is something to draw; this is the other half,
  // and without it the two disagree silently.
  //
  // WHICH IS AN INFINITE LOOP, not an untidiness. setFitting writes into `bySlot[book.at]` —
  // the slot the store thinks is open — while the effect that fills a blank slot asks whether
  // `bySlot[slot.key]` is empty. Let those be different keys and the suggestion lands
  // somewhere the guard never looks: the book is rewritten, `bySlot` is a new object, the
  // effect's deps changed, and it applies the suggestion again. Forever, from the moment the
  // manual is opened, which is what "Maximum update depth exceeded" was.
  //
  // One write and it converges: the key matches from the next render on and this does nothing.
  useEffect(() => {
    if (slot && here !== slot.key) goToSlot(docId, slot.key);
  }, [docId, here, slot]);
  // THE SLOT ON SCREEN, and not the key the store happens to hold. Those differ on a document
  // nobody has navigated: `at` starts as the empty sentinel, which is no longer a slot, and
  // the phase falls through to the first sub-step. Compared against the raw value, a call
  // asked from that opening view would come back to a key that never matched and be dropped
  // as "the phase has moved on" — from the one place it had not.
  const hereRef = useRef(slot?.key ?? '');
  hereRef.current = slot?.key ?? '';

  // WHY A SUB-STEP CANNOT BE DRAWN, told apart instead of guessed at. Two entirely different
  // states used to share one sentence joined by "or": Phase 4 never said which part moves
  // here, or it said and the parts it named cannot be drawn. The first is fixed by re-running
  // Phase 4 and the second by reconstructing a part in Phase 5, and a reader shown both at
  // once takes the first — which is the wrong one nearly every time, because hardware is
  // exactly what Phase 5 tends not to have meshed.
  //
  // So the parts Phase 4 DID give a role to are checked one by one, and the ones with nothing
  // to draw are named. Off the chips rather than off `suggests`, which is the same answer
  // resolved to catalog ids and therefore cannot tell a part with no mesh from a part the
  // catalog has never seen — two states, one empty selector, and the same sentence fixes both.
  //
  // BOTH ENDS, for every gesture. A receiving part used to be excused on a placing, which was
  // fitted to nothing and drew one mesh; both ends are drawn now, so a receiving part with no
  // reconstruction is exactly as missing from the picture as an added one.
  const undrawable = (slot?.parts ?? []).filter((p) => (
    !p.tool
    && (p.role === 'added' || p.role === 'receiving')
    && (p.itemId == null || !modelOf.has(p.itemId))
  ));

  // WHAT PLACES THIS SUB-STEP: two pieces of hardware as their set built them (fromSet), a component
  // part as its joint has it (fromJoint), a piece of hardware into its hole (fromHole). Nothing
  // else does — no model is asked to guess a movement no rule can work out.
  //
  // EACH RULE IS ABOUT SOME SUB-STEPS AND NOT OTHERS (null), and about those it places or says why
  // it cannot (Declined). The set is asked first; where it is about the sub-step, its word is the
  // only one said, since the joint's and the hole's are about other gestures.
  const labelOf = (id: number | null) => (id != null ? byItem.get(id)?.label : undefined) ?? `#${id}`;
  const setTried = slot ? setPlacement(
    slot.subKind, modelOf.get(fitting.movingId ?? -1), modelOf.get(fitting.targetId ?? -1), manualSets,
    { moving: labelOf(fitting.movingId), target: labelOf(fitting.targetId) },
  ) : null;
  const jointTried = useJointPlacement(docId, slot && !setTried ? slot.key : null);
  const holeTried = useHolePlacement(docId, slot && !setTried ? slot.key : null);
  const fromSet = isDeclined(setTried) ? null : setTried;
  const fromJoint = isDeclined(jointTried) ? null : jointTried;
  const fromHole = isDeclined(holeTried) || fromJoint ? null : holeTried;
  // WHY NOTHING PLACES IT, where nothing does: the reason of the first rule that is about it, in
  // the order they are tried — a rail carrying its barrel nut is the joint's, and the hole's word
  // about the nut would only be noise. Said too where no rule is about this kind of gesture.
  // AND WHERE WHAT IS DRAWN IS AN OLD MODEL ANSWER, which no rule has replaced, it says so.
  const why = [setTried, jointTried, holeTried].find(isDeclined);
  // NOT BEFORE THE PARTS, THE STEPS AND THE MODELS ARE IN: until then no rule recognises anything,
  // and "no rule places this kind of sub-step" flashed on every refresh.
  const noRule = !jobsLoaded || !partsResolved || !slot?.letter || leader || fromSet || fromJoint || fromHole ? null
    : [why ? sayDeclined(why, tr) : tr('notice.rule.noneForKind')]
      .concat(fitting.placedBy === 'said' ? [tr('notice.rule.oldAnswer')] : []);
  const [settingFromSet, setSettingFromSet] = useState(false);
  // Why placing from the set failed — its shaft's model would not load — shown where a call's
  // failure is.
  const [setError, setSetError] = useState<string | null>(null);
  const callSlot = useSlot(ASSEMBLY_CALL_SLOT_ID);
  // The manual's own wording, in the language on screen — falling back to the other rather
  // than to nothing, since a step read in one language only still says what it does.
  const slotText = (language === 'fr' ? slot?.textFr : slot?.textEn)
    || slot?.textEn || slot?.textFr || '';
  // AND WHAT IS SAID UNDER THE PICTURE AT THIS MOMENT: the beat of the sentence for the slice being
  // played, with "1/2" before it (see captionAt).
  const beat = captionAt(language === 'fr' ? slot?.beatsFr : slot?.beatsEn, slotText, played, t, slot?.beatItems);
  // THE KIND CHIP'S WORDS, from the sub-step's own kind where Phase 4 gave one and from the
  // family otherwise. Off the SLOT and not off the fitting, unlike `placing` far above: the
  // fitting keeps a family because the controls need one on every slot, while the number is
  // the analysis's answer and is not something this phase writes, adopts or defaults.
  const kindWords = subStepKindWords(slot?.subKind, slot?.kind, tr);
  // THE SLOT'S OWN PARTS, as the analysis names them TODAY. The universe the chips are drawn
  // from and the dropdowns are cut out of — read from the slot on every render, never stored,
  // so a re-run of Phase 4 that adds or removes a part is reflected the instant it lands.
  // PARTS ONLY. The row now shows the tools too — it is Phase 4's list, and Phase 4 shows
  // both — but a tool is not a candidate for "the part that moves" or "the part it is fitted
  // onto": it is not fitted anywhere and is not consumed. It has its own selector, offered
  // the whole manual on purpose (see useDocTools).
  const slotItems = useMemo(() => slotPartIds(slot), [slot]);
  // Ticked is what the sub-step names MINUS what somebody unticked, which is why the default
  // is everything: a part the analysis puts in this sub-step is in it, and starting from an
  // empty row made the commonest case — use them all — a click per chip.
  const ticked = useMemo(
    () => new Set([...slotItems].filter((id) => !fitting.excluded.includes(id))),
    [slotItems, fitting.excluded],
  );

  // WHAT THE TWO DROPDOWNS MAY OFFER was computed here — the ticked parts of the slot, and
  // nothing else. There are no dropdowns: the two parts come from Phase 4's roles now, so
  // there is no list to cut down. Ticking is still what says which parts this fitting is
  // about, and it is still read — by the effect below, which drops a part the row no longer
  // offers, and by the scene, which draws what is ticked.

  // AND NOTHING STAYS SELECTED THAT THE ROW ABOVE NO LONGER OFFERS. A stored fitting names two
  // parts and the analysis it was written against can change under it — re-running Phase 4
  // rewrote 1A's sentence and its parts, and the selectors went on showing the barrel nut from
  // the old answer. A value in a dropdown that is not in the list it is drawn from is not a
  // choice, it is a leftover, and it would have been animated and published as one.
  //
  // Cleared at the source rather than hidden at the draw, so what is on screen is what is
  // stored — and cleared through `set`, so the sign-off goes with it: the animation somebody
  // validated was of two parts, and one of them is gone.
  //
  // Only where the slot HAS a list to be absent from: a sub-step whose parts have not landed
  // yet would otherwise wipe a good answer.
  // WHICH KIND THE SLOT IS, IS THE ANALYSIS'S ANSWER AND NOT A SETTING — so it is adopted
  // here rather than offered as a control. Phase 4 read the sub-step's own sentence; nobody
  // on this page is being asked to disagree with "lay the frame flat" about whether anything
  // is being joined.
  //
  // NOTHING GOES WITH IT ANY MORE. A sub-step re-read as a placing used to lose the part it
  // was being fitted onto and the tool that drove it — both being things a placing was held
  // not to have. A placing has two parts like everything else now (see fittingChosen), and a
  // gesture that fastens nothing simply lists no tool, so there is nothing here to clear:
  // what changes is the word, and through `write` the sign-off goes with it, because the
  // animation somebody validated was of another kind of movement.
  //
  // Only where the analysis HAS said: on a sub-step from before the word was asked for, the
  // stored kind stands.
  useEffect(() => {
    const kind = slot?.kind;
    if (!kind || kind === fitting.kind) return;
    write({ kind });
  }, [docId, slot?.kind, fitting.kind]);

  // AND ONLY ONCE THE PARTS ARE RESOLVED. A piece an earlier sub-step made is known at once (its
  // id is its number) while a supplied part's catalog row arrives a moment later — so a sub-step
  // receiving a piece had a list holding the piece alone for that moment, and the barrel nut signed
  // off in it was "gone": cleared, and the sign-off with it, on every opening of the page.
  useEffect(() => {
    if (!partsResolved || slotItems.size === 0) return;
    const gone = (id: number | null) => id != null && !ticked.has(id);
    if (!gone(fitting.movingId) && !gone(fitting.targetId)) return;
    write({
      ...(gone(fitting.movingId) ? { movingId: null } : {}),
      ...(gone(fitting.targetId) ? { targetId: null } : {}),
    });
  }, [docId, partsResolved, slotItems, ticked, fitting.movingId, fitting.targetId]);

  // What the scene has to download. Keyed by the two item ids alone, so nudging a seat or
  // dragging the scrubber never remounts the viewer — the meshes are megabytes, and a
  // reload per keystroke is the difference between a control you can feel and one you fight.
  const freshParts = useMemo<ScenePart[] | null>(() => {
    if (!fittingChosen(fitting)) return null;
    // A cache buster and not a flag, because it is the FETCH being retried: the browser holds
    // the address, and asking for the same one again is how you get the same failure back.
    const asked = (url: string) =>
      (attempt ? `${url}${url.includes('?') ? '&' : '?'}retry=${attempt}` : url);
    // AND THE STATE OF THE FILE IN THE ADDRESS, which is a different question from the retry
    // above and was the answer to "why is the part still lying on its side here". A mesh is
    // served from one unchanging URL and every edit overwrites what is behind it, so a phase
    // that asked for the bare address got whatever the browser had cached from before the
    // turn — for as long as the cache held it, which is days. meshStamp moves with every
    // write; see _apply_edit, which is what moves it.
    const at = (job: Job3D) => asked(meshSrc(job.id, meshStamp(job)));
    const pick = (id: number | null, instanceId: string): ScenePart | null => {
      if (id == null) return null;
      const job = modelOf.get(id);
      const meta = byItem.get(id);
      if (!job) return null;
      // A PIECE IS DOWNLOADED AS THE PARTS IT IS MADE OF (see ScenePart.pieces). Each at its own
      // model's stamp where that model is still a part's — and it is, or the piece would not be
      // here to draw (see useDrawableParts).
      const made = madeOf(job)?.map((q) => {
        const of = [...modelOf.values()].find((m) => m.id === q.job);
        return { url: of ? at(of) : asked(meshSrc(q.job)), m: q.m, name: q.name };
      });
      return {
        instanceId,
        url: at(job),
        ...(made ? { pieces: made } : {}),
        // The other hand of a pair: the same file, reflected (see Fitting.mirrored).
        ...(instanceId === 'moving' && fitting.mirrored ? { mirror: true } : {}),
        dims: sceneDims(job, meta?.dims),
        trueScale: isTrueScale(job),
        // And what the run measured, for a part with no printed size — see mmScale. A mesh
        // left in file units stands a thousand times too big beside one in millimetres, so
        // every place that mounts a part has to anchor it the same way.
        builtMm: builtBoxOf(job),
        label: meta?.label ?? `#${id}`,
      };
    };
    // BOTH PARTS, whatever the family. A placing downloaded one — the part that moves — and
    // drew a side rail alone in an empty room while the frame it is held against was named in
    // the answer and never fetched.
    const wanted = [pick(fitting.targetId, 'target'), pick(fitting.movingId, 'moving')];
    if (!wanted.every(Boolean)) return null;

    // AND THE TOOL, WHICH IS NOW ONE OF THE THINGS ON SCREEN. Optional in a way the parts are
    // not: a fitting without its two parts is not a fitting and draws nothing, while a
    // screwdriver the Parts DB has never seen — most allen keys — leaves the rest of the
    // picture exactly as it was. So it is appended if it resolves and dropped if it does not,
    // rather than being allowed to hold the viewer empty.
    // AND THE SECOND, for the second hardware (see Fitting.nextToolId).
    const drawn = ([['tool', fitting.toolId], ['tool2', nextOf(fitting) ? fitting.nextToolId : null]] as const).flatMap(([instanceId, id]) => {
      const tool = tools.find((t) => t.value === id);
      const job = tool?.itemId != null ? modelOf.get(tool.itemId) : null;
      return job && tool ? [{
        instanceId,
        url: at(job),
        dims: sceneDims(job, tool.dims),
        trueScale: isTrueScale(job),
        builtMm: builtBoxOf(job),
        label: tool.label,
      }] : [];
    });
    // AND EVERY OTHER COPY OF THE MOVING PART — the same file, drawn once more per copy (see
    // Fitting.copies); the scene fetches it once.
    const copies = (fitting.copies ?? []).map((_, n) => ({ ...(wanted[1] as ScenePart), instanceId: copyId(n + 1) }));
    // AND THE HARDWARE PREPARED ON EACH OF THEM IN THE HAND (see Fitting.prepOn), where it is placed.
    const prep = prepOf(fitting);
    const washer = prep ? pick(prep.itemId, prepId(0)) : null;
    const prepared = washer ? prepIds(fitting).map((id) => ({ ...washer, instanceId: id })) : [];
    // AND THE SECOND HARDWARE, into the part fitted onto (see Fitting.nextOn).
    const next = nextOf(fitting);
    const dowel = next ? pick(next.itemId, nextId(0)) : null;
    const after = dowel ? nextIds(fitting).map((id) => ({ ...dowel, instanceId: id })) : [];
    // AND THE HARDWARE PREPARED ON EACH OF THOSE (see Fitting.nextPrep).
    const nextPrep = nextPrepOf(fitting);
    const ring = nextPrep ? pick(nextPrep.itemId, nextPrepId(0)) : null;
    const onNext = ring ? nextPrepIds(fitting).map((id) => ({ ...ring, instanceId: id })) : [];
    // AND THE THIRD, after it (see Fitting.lastOn).
    const last = lastOf(fitting);
    const cover = last ? pick(last.itemId, lastId(0)) : null;
    const covers = cover ? lastIds(fitting).map((id) => ({ ...cover, instanceId: id })) : [];
    const all: ScenePart[] = [...(wanted as ScenePart[]), ...copies, ...prepared, ...after, ...onNext, ...covers, ...drawn];
    // THE GESTURE'S MESHES REFLECTED, on a repeat on the other side (see Fitting.mirrorAll): the hand
    // already reflected comes back. NOT THE FIXED PART (see mirroredMesh).
    const shown = mirrorAll ? all.map((p) => (mirroredMesh(p.instanceId) ? { ...p, mirror: !p.mirror } : p)) : all;
    // AND AT THE STATES THIS SUB-STEP DRAWS THEM AT (see clipParts).
    return shown.flatMap((p) => {
      const of = clipParts?.find((c) => c.instanceId === p.instanceId);
      return of ? withStates(p, of, meshAtSrc) : [p];
    });
  }, [fitting.targetId, fitting.movingId, fitting.toolId, fitting.nextToolId, fitting.mirrored, fitting.copies?.length, fitting.prepOn, fitting.nextOn, fitting.nextPrep, fitting.lastOn, mirrorAll, tools, modelOf,
    byItem, attempt, clipParts]);
  // THE SAME PARTS ARE THE SAME SCENE: rebuilt when what is drawn changes, not when the lists it is
  // read from are fetched again. Signing a sub-step off saves its piece, the pieces are fetched
  // again, every list is new, and the scene was torn down and loaded afresh around the same meshes.
  const partsSig = freshParts ? JSON.stringify(freshParts) : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sceneParts = useMemo(() => freshParts, [partsSig]);

  // WHAT EACH PART'S MODEL IS NOW, marked the way a sign-off keeps it — see fittingModels. The
  // tick writes these; the warning under the viewer compares them with the ones it wrote.
  const toolItemId = tools.find((tool) => tool.value === fitting.toolId)?.itemId ?? null;
  const signedNow = useMemo(
    () => marksNow(fitting, modelOf, toolItemId),
    [fitting.targetId, fitting.movingId, modelOf, toolItemId], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Not before the models have loaded: an empty map would read as every part changed.
  const changed = jobsLoaded ? changedSince(fitting.signedOn, fitting, modelOf, toolItemId) : [];
  const changedLabels = changed.map((role) => (role === 'tool'
    ? tools.find((tool) => tool.value === fitting.toolId)?.label
    : byItem.get((role === 'target' ? fitting.targetId : fitting.movingId) ?? -1)?.label) ?? role);

  // WHY THE TOOL IS NOT ON SCREEN, when the sub-step calls for one and the viewer has none.
  //
  // The tool is appended to the scene only when it resolves to a mesh, and dropped otherwise —
  // which is right, since a screwdriver the Parts DB has never seen must not hold the whole
  // viewer empty. What was wrong is that it was dropped IN SILENCE: the instructions say
  // "adjust it using the PVC screwdriver", the panel lists the tool among the parts needed,
  // and the picture showed two parts and no account of the third. A reader cannot tell that
  // from a manual that never mentioned a tool.
  //
  // Two ways it goes missing and they want different answers: nobody has chosen one, or the
  // chosen one has no reconstruction to draw. Null when the sub-step asks for no tool, which
  // is most of them, every placing among them, and is not worth a line — the family needs no
  // test of its own for that.
  const toolGap = useMemo(() => {
    // NOT BEFORE THE MODELS AND THE PARTS ARE IN: until then every tool reads as one with no mesh,
    // and the line flashed on every refresh.
    if (!jobsLoaded || !partsResolved) return null;
    const asked = slot?.tool ?? null;
    if (!asked && fitting.toolId == null) return null;
    if (fitting.toolId == null) {
      return { why: 'none' as const, label: tools.find((t) => t.value === asked)?.label ?? '' };
    }
    const tool = tools.find((t) => t.value === fitting.toolId);
    if (!tool) return { why: 'none' as const, label: '' };
    const job = tool.itemId != null ? modelOf.get(tool.itemId) : null;
    return job ? null : { why: 'nomesh' as const, label: tool.label };
  }, [slot?.tool, fitting.toolId, tools, modelOf, jobsLoaded, partsResolved]);

  useEffect(() => {
    view.current = null;
    setUnscaled([]);
    if (!host.current || !sceneParts) { setState('idle'); return; }
    return mountAssembly(host.current, sceneParts, {
      // ITS SHOTS AT THE READER'S ZOOM in either shape (see AssemblyOpts.framedAt).
      framedAt: SHOT_RATIO,
      // THE READER'S LIGHT (see READER_BRIGHTNESS), so a dark frame reads here as it does there.
      onReady: (v) => { view.current = v; v.brightness(READER_BRIGHTNESS); },
      // The same three words the mesh viewer writes on its own gizmo, paired to THIS frame:
      // the assembly is Z-up as Blender is, so Z is the height and Y is the depth. The mesh
      // viewer's gizmo stands in three's Y-up world and pairs the last two the other way
      // round — same vocabulary, and the root's quarter turn is what swaps them.
      axisWords: words,
      onView: (v) => { liveView.current = v; viewAngles.current?.(v); },
      extentWords,
      faceWords,
      // A CLICK ON THE WHITE PUTS THE PART DOWN. A CLICK ON THE PART DOES NOT.
      //
      // It used to toggle — press the selected part again and the panel closed — on the
      // argument that the way out of a selection should be where the hand already is. The
      // hand is on the part precisely because that is the part being worked on, and a press
      // that goes nowhere is a click: every aborted drag, every time the pointer moved less
      // than four pixels before it came up, threw away the selection somebody was in the
      // middle of using. The two gestures share a button and only one of them can own it.
      //
      // So the way out is the empty white around the assembly, the panel's own cross, and the
      // card's name — three of them, none ambiguous, and all reachable without letting go of
      // what you were doing.
      //
      // Through the updater rather than by reading `picked`: this handler is baked into the
      // viewer at mount and never rebuilt, so the `picked` it would close over is the one
      // from the render that mounted it — null, for ever. Returning `was` unchanged for a
      // re-press keeps that press from costing a render.
      onPick: (pick) => setPicked((was) => {
        if (!pick) return null;
        if (was?.instanceId === pick.instanceId) return was;
        return { instanceId: pick.instanceId };
      }),
      onUnscaled: setUnscaled,
      onState: setState,
      // AS ON THE PUBLISHED PAGE: a drag on the parts turns them, a drag beside them slides the
      // camera. It is also what makes the scene say the camera was taken at all.
      onCameraTaken: () => { taken.current = true; },
    });
    // `words` and not `tr`: the gizmo's labels are baked into a scene at mount, so the
    // viewer does have to be rebuilt when the language changes — but `tr` is a fresh
    // closure on every render of the language provider, so depending on it meant
    // remounting for reasons that had nothing to do with language. A remount mid-load is
    // what left a dead canvas over the live one; this removes the commonest cause of one.
    // AND `atEnd`: the end's panel takes this viewer's box off the page, and the scene mounted in
    // it with it — coming back to the same sub-step has the same parts and a new box.
  }, [sceneParts, words, extentWords, faceWords, atEnd]);

  // The fitting changed shape, so re-fit the camera — once, here, and never while
  // scrubbing. A shot that re-framed per frame would push in as the part approached, and a
  // part cannot be seen to travel if the world travels with it.
  useEffect(() => {
    if (state !== 'ready') return;
    // Unless a part was just placed by hand (see `held`): re-aiming there would move the world
    // out from under it.
    if (held.current) held.current = false;
    else {
      taken.current = false;
      view.current?.frame(asm, STEP);
      // AND THE RECORDED SHOT BACK ON TOP OF THE FRESH FIT, because `frame` ends at HOME —
      // square on the origin, 0/0/0 — and home is a distance answer that also throws away the
      // angle. So the moment a call landed with a camera in it, the fitting was written, the
      // summary printed −25° 60° 0°, this effect re-framed for the poses that had just
      // changed, and the viewport went straight back to square-on: the angle stored, shown,
      // and nowhere on screen.
      view.current?.look(camAt(withTried(playedRef.current, triedRef.current), t));
    }
    showAt(t);
    // `t` deliberately absent: this runs when the GEOMETRY changes, not when time does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawnAsm, state]);

  // THE OUTLINE IS THE SELECTION, DRAWN. Kept here rather than inside the scene's click
  // handler so there is one answer to "what is selected" — the panel and the orange rim are
  // the same fact, and a scene that outlined on its own could disagree with the panel the
  // moment anything else cleared it.
  useEffect(() => {
    view.current?.select(picked?.instanceId ?? null);
  }, [picked?.instanceId, state]);

  // A slot's parts are that slot's; arriving somewhere else with the last one still outlined
  // would point at a part that is no longer on screen. What was hidden goes with it, for the
  // same reason and one more: the next sub-step's parts are different parts, and starting on
  // one of them missing would be a picture nobody chose.
  useEffect(() => {
    setPicked(null);
    setHidden([]);
    setSetError(null);
  }, [here, fitting.movingId, fitting.targetId]);

  useEffect(() => {
    if (clipParts?.length && statesAt(clipParts, drawnAsm, t)) showAt(t);
    else view.current?.hide(hidden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, state]);
  // THE SHOT PLAYS WITH THE MOVEMENT. Two ends were recorded and nothing flew between them:
  // the camera held wherever it was and the pair was a note rather than a shot.
  //
  // ON `t` AND NOTHING ELSE, which is the whole of the rule and the reason it does not fight
  // anybody. Moving the transport — playing, scrubbing, rewinding — is asking to WATCH, and
  // watching is what the recorded view is for; between two values of t the camera is yours to
  // drag wherever you like, and it stays there. Re-applying whenever the stored shot changed
  // would be worse than useless: pressing record at t=0.5 writes the view on screen and would
  // immediately be answered by the interpolated one, moving the camera off what was just
  // recorded.
  //
  // Read through `latest` rather than off `fitting`, so editing a camera field does not
  // count as a reason to re-aim.
  //
  // AND NOT WHILE IT PLAYS, which is what made the viewer refuse to be zoomed mid-movement.
  // `t` advances once a frame during playback, and this fires on every value of it — so a
  // wheel that zoomed in was answered a sixteenth of a second later by the recorded shot, at
  // its own zoom, sixty times a second. The camera is one shot at both ends now (FIXED_CAM),
  // so re-aiming through a playback changes nothing except undoing whoever is looking closer.
  //
  // READ OFF A REF, deliberately: `playing` in the dependency list would re-aim the moment it
  // went false, snapping the camera back to the recorded shot the instant the movement ended —
  // which is the same theft one second later. Scrubbing and rewinding still re-aim, because
  // those change `t` while this is false.
  //
  // AND NOT ONCE SOMEBODY HAS TAKEN THE CAMERA — an orbit, a pan, a wheel — as on the published
  // page (see ClipPlayer's `taken`). Scrubbing is how a movement is looked at closely, and the
  // close view was thrown away by the first touch of the transport. Theirs until the picture is
  // framed afresh, which is where this is put back.
  //
  // AND NOT ON THE FRAME THAT ENDS A PLAYBACK EITHER. The clock sets t to 1 and stops in one go
  // (see usePlayClock), so the render that ends the movement already reads `playing` false, and
  // the shot was re-aimed on its last frame: 1-1 played at the view on screen and jumped to its
  // recorded ×3.18 as it finished. `wasPlaying` is the render before's, set after this effect.
  const playingRef = useRef(false);
  playingRef.current = playing;
  const wasPlaying = useRef(false);
  // UNLESS THE SHOT MOVES DURING THE GESTURE: the barrel nuts' own, then the rail's (see camAt).
  useEffect(() => {
    if (state !== 'ready' || taken.current) return;
    const flies = !!prepOf(playedRef.current)?.shot || !!nextOf(playedRef.current)?.shot || !!triedRef.current;
    if (!flies && (playingRef.current || wasPlaying.current)) return;
    view.current?.look(camAt(withTried(playedRef.current, triedRef.current), t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, state]);
  useEffect(() => { wasPlaying.current = playing; }, [playing]);

  // Each part's own box, switched by its own card — the same shape as the origin crosses
  // below, and re-sent whenever the parts change: a scene builds a box per part at load, so
  // a new pair of meshes arrives with all of them on.
  //
  // WHAT PLAYING DOES TO THESE IS NOT DONE HERE ANY MORE. It was a suppression — `playing ? []
  // : …` on each of the three, with the switches left as they were — so the furniture came
  // back by itself the instant the movement stopped, which is the one moment nobody wants it:
  // the picture you are looking at when it ends is the picture you were watching. Pressing
  // play now WRITES the switches off (see `toggle`), so what is on screen and what the buttons
  // say are the same fact, and turning any of it back on is a press like every other.
  useEffect(() => {
    view.current?.measures(
      (sceneParts ?? []).map((p) => p.instanceId).filter((id) => !rulersOff.includes(id)),
    );
  }, [rulersOff, sceneParts, state]);
  // The same again for the six names.
  useEffect(() => {
    view.current?.faces(
      (sceneParts ?? []).map((p) => p.instanceId).filter((id) => !facesOff.includes(id)),
    );
  }, [facesOff, sceneParts, state]);
  useEffect(() => { view.current?.gizmo(gizmoOn); }, [gizmoOn, state]);

  // AND THE PART OF THE PICTURE THAT IS WRONG, PAINTED RED — at whatever moment is on screen,
  // including while it plays (see useClashPaint, which asks for the faces of that moment).
  //
  // UNLESS SWITCHED OFF, from the collisions' own card (see ClashBar): the reading stays, the red goes.
  // OFF BY DEFAULT (Louis 2026-10-22): switched on from the card when wanted.
  const [clashMarked, setClashMarked] = useState(false);
  useClashPaint(view, asm, STEP, clash.profile, t, clashMarked);


  // A PART TAKEN OUT OF THE PICTURE CANNOT BE THE ONE BEING POSITIONED. The panel would go on
  // showing numbers for something not on screen, and its outline would be drawn on nothing.
  useEffect(() => {
    if (picked && hidden.includes(picked.instanceId)) setPicked(null);
  }, [hidden, picked]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => showAt(t), [drawnAsm, t]);

  // WHAT THIS SUB-STEP MAKES is saved as a mesh of its own — see the sign-off below, which is
  // what calls it, and saveSubstepOutput, which files it beside every other run.
  const keepOutput = async () => {
    const scene = view.current;
    if (!scene || !slot) return;
    // THE END OF THE MOVEMENT, which is what was signed off, read without being shown: the scene
    // is put at t=1, read, and put back in the same tick, so no frame of it is ever drawn. The
    // scrubber stays where it was and a clip that was playing plays on (Louis 2026-10-28). It
    // used to stop the clip and run the scrubber to the end, to bake the picture on screen.
    showAt(1);
    let ended: ReturnType<AssemblyView['placed']>;
    try { ended = scene.placed(); } finally { showAt(t); }

    try {
      // THE FINISHED POSITION, AS THE MESHES IT IS MADE OF: each part by its run, where it stands,
      // and the mark of the model it is (see modelMark). A part that is itself a piece goes in as
      // ITS parts, moved with it — the list is always of meshes.
      const pieces = ended.flatMap(({ instanceId, piece, m }): PiecePart[] => {
        // NOT THE TOOL: it is put down once the gesture is done, and is no part of what it made.
        if (isTool(instanceId)) return [];
        // Every copy of the moving part is the moving part's model (see Fitting.copies).
        const job = modelOf.get((instanceId === 'target' ? fitting.targetId
          : isMovingCopy(instanceId) ? fitting.movingId
            : isPrep(instanceId) ? prepOf(fitting)?.itemId ?? null
              : isNext(instanceId) ? nextOf(fitting)?.itemId ?? null
                : isNextPrep(instanceId) ? nextPrepOf(fitting)?.itemId ?? null
                  : isLast(instanceId) ? lastOf(fitting)?.itemId ?? null : toolItemId) ?? -1);
        if (!job) return [];
        const own = piece == null ? null : madeOf(job)?.[piece];
        if (piece != null && !own) return [];
        return [own ? { ...own, m } : {
          job: job.id,
          m,
          name: sceneParts?.find((p) => p.instanceId === instanceId)?.label ?? '',
          mark: modelMark(job, liveOf(modelOf)),
        }];
      });
      if (!pieces.length) return;
      // ONE PIECE, FILED ONCE PER SUB-STEP THAT MAKES THIS GESTURE — this one alone, or all six
      // where six of them are the same movement.
      //
      // The picture is identical; the PIECES are not. 1AA leaves "101 – First prepared screw"
      // and 1AD leaves "104 – Fourth prepared screw", and the sub-step that picks 104 up three
      // gestures later finds its model through 1AD's own slot (a piece is filed by slot). File
      // the leader's alone and five later sub-steps have a part with no model — the repeats would have cost the manual the very pieces they were meant to save
      // the work of making.
      // A REPEAT ON THE OTHER SIDE IS FILED AS ITS LEADER'S PIECE, NOT REFLECTED: the right rail
      // with its nuts is drawn from the left one by the sub-step that uses it, whose joint rule
      // reflects the moving part by its hand (see fromJoint). Filed reflected, it was reflected
      // twice and the nuts came out on the outer face.
      for (const each of run.length ? run : [slot]) {
        await saveSubstepOutput({
          docId,
          slot: each.key,
          // The output's own name where Phase 4 gave it one, the sub-step's label otherwise —
          // this row has to name itself in a strip beside a dozen parts.
          label: each.output
            ? `${each.output.id} – ${language === 'fr' ? each.output.fr : each.output.en}`
            : each.label,
        }, pieces);
      }
      // What the sub-steps after this one stand on has changed (see useDrawableParts).
      bumpSubstepOutputs(docId);
    } catch (e) {
      // SAID, not logged: the sub-steps after this one stand on this piece, and a save that failed
      // in silence left a sub-step signed off with nothing for the next to be drawn on.
      console.warn('[assembly] the output mesh could not be saved', e);
      setSetError(`${tr('notice.assembly.bakeFailed')} ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // SLIDE IT HOME, ONCE, WHEN A CONTACT HAS JUST LANDED.
  //
  // The contact puts one named point on the other and the two BOXES touch; the surfaces do
  // not, because a box face is a plane through the furthest point of a curved part. So the
  // last number is measured rather than named: the part is slid along the approach until the
  // clearance lands in the tolerance band. See assemblySnap.
  //
  // ONLY ON `seated === false`, which is only ever written by a contact landing — a fitting
  // somebody typed, or one from an answer that predates contacts, has no such flag and is left
  // exactly where it stands. The write that finishes this sets it true, so this runs once and
  // the effect that follows it does nothing.
  //
  // AND ONLY WITH THE MESHES ON SCREEN, since it measures triangles: `state` is what says so.
  // A repeat is somebody else's answer and is never moved from here (see `write`), so the pass
  // is not started on one at all rather than started and refused.
  const [seating, setSeating] = useState(false);
  useEffect(() => {
    if (leader || state !== 'ready' || fitting.seated !== false) return;
    if (!fittingChosen(fitting)) return;
    let live = true;
    setSeating(true);
    void seatOnSurface(view.current!, movementOf(fitting))
      .then((at) => {
        if (!live) return;
        // A CONTACT WHOSE SURFACES NEVER MEET is left exactly as it was answered, and marked
        // done so this does not walk the same three hundred millimetres again on every
        // render. It is a contact on the wrong face — a thing to see, not to hide by sliding
        // the part somewhere plausible.
        // The slide is the contact's last number, not a hand's: whose placement it is stays.
        write({ ...(at ?? {}), seated: true, placedBy: fitting.placedBy });
        setSeating(false);
      })
      .catch(() => { if (live) { write({ seated: true }); setSeating(false); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, fitting.seated, leader]);

  // Advanced against the wall clock (see usePlayClock), one sub-step at the clip's own speed.
  usePlayClock(playing, setPlaying, setT, clipMs(played) / SPEED / speed);

  // Pressing play at the end replays from the start, which is what the button means there —
  // otherwise it advances t past 1 and stops again on the same frame.
  //
  // AND PLAYING PUTS EVERY PART DOWN. A selection is a part being worked on: it wears an
  // orange rim, it opens a panel over the shot, and its rulers and cross are the same
  // measurements the playback effects above strip for the same reason — they are furniture
  // around a still, and a movement is the one thing on screen while it runs. Pausing does not
  // pick anything back up; nothing was taken away, the hand just reaches again.
  // EVERYTHING DRAWN ABOUT THE PARTS, OUT OR BACK. Two callers write exactly this — the play
  // button below, and the button in the corner of the viewport — and they differ in when they
  // press it, never in what it says. Held apart, one of them would learn a fifth mark and the
  // other would not.
  //
  // `on` is what to leave the picture in, so the corner button is this function twice.
  const marks = useCallback((on: boolean) => {
    const ids = on ? [] : (sceneParts ?? []).map((p) => p.instanceId);
    setRulersOff(ids);
    setFacesOff(ids);
    setGizmoOn(on);
  }, [sceneParts]);

  // EVERY SUB-STEP OPENS WITH ITS MARKS OFF, whatever the last one was left with — set before the
  // frame is painted, so the rulers are never drawn and then taken away.
  // Keyed on WHICH parts, not on the list: it is rebuilt whenever a model loads, and that is not a
  // new sub-step.
  const partIds = sceneParts?.map((p) => p.instanceId).join(',') ?? null;
  useLayoutEffect(() => {
    if (partIds != null) marks(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.key, partIds]);

  // AND WHETHER ANY OF IT IS ON, which is not a state of its own: the cards and the gizmo
  // button own those switches, and a `bare` flag beside them would be a second answer to the
  // same question — one the day somebody turns a single cross back on and the corner button
  // still claims the picture is clear.
  const marksOn = gizmoOn
    || rulersOff.length < (sceneParts?.length ?? 0)
    || facesOff.length < (sceneParts?.length ?? 0);

  const toggle = useCallback(() => {
    if (playing) { setPlaying(false); return; }
    // A MOVEMENT THAT CANNOT BE TRUE IS NOT PLAYED. See overThread: a thread deeper than the
    // travel makes assemblyOf draw the part from somewhere the tables do not say it starts,
    // so what would run is a movement nobody described. The scrubber still works — the frames
    // are still drawn, and being able to look at the wrong one is how it gets fixed.
    if (overThread) return;
    if (t >= 1) setT(0);
    setPicked(null);
    // AND EVERY MARK ON THE PICTURE GOES OUT — the rulers, the six face names, the origin
    // crosses and the gizmo at the world's origin. Each is a reading of something standing
    // still, and eight of them travelling with two parts is the movement hidden behind its own
    // annotations.
    //
    // WRITTEN, NOT SUPPRESSED, which is the whole of the change. Held off only for the length
    // of the clip, they all came back the moment it stopped — on the frame somebody had just
    // watched arrive, which is the frame they were looking at. Off is now the state: the cards
    // and the gizmo button say so, and whatever is wanted back is one press away.
    marks(false);
    setPlaying(true);
  }, [playing, t, marks, overThread]);

  const set = (patch: Partial<Fitting>) => { setPlaying(false); write(patch); };

  // WHAT A SUGGESTION LOOKS LIKE AS AN ANSWER. One function and two callers — arriving on a
  // slot nobody has answered, and the button that goes and asks for one — because they differ
  // in WHEN they may write, never in what they write. Held apart, they drifted the day either
  // learned a new field.
  //
  // It writes into the OPEN slot (see setFitting), so both callers make sure the slot they
  // mean is the one on screen before they call it.
  // WHERE EVERY OTHER PART OF THIS FITTING STANDS — the piece being fitted onto, and the tool
  // if one is in hand. Same reading as the middle column above and in the same units, for
  // parts that have one pose rather than two.
  const still = useMemo(() => {
    const read = (instanceId: string, label?: string) => {
      const pose = poseOf(instanceId);
      return pose && label
        ? {
          id: instanceId,
          label,
          pose: {
            pos: pose.pos.map(asCm),
            rot: eulerDegFromQuat(pose.rot).map((v) => Math.round(v * 100) / 100),
          },
        }
        : null;
    };
    return [
      read('target', fitting.targetId != null ? byItem.get(fitting.targetId)?.label : undefined),
      read('tool', tools.find((x) => x.value === fitting.toolId)?.label),
    ].filter((x): x is NonNullable<typeof x> => !!x);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed, fitting.targetId, fitting.toolId, byItem, tools]);

  // A SLOT NOBODY HAS ANSWERED, GIVEN ITS PARTS: the two Phase 4 names, its gesture's kind and its
  // tool. Nothing about where they stand — that is a rule's to work out, and the fitting is left as
  // BLANK has it so that the rule places it on arrival (see awaitsPlacing).
  const applySuggestion = (s: Pick<Slot, 'kind' | 'suggests' | 'tool'>) => {
    // Only what can be drawn: a part with no model in the Parts DB is a value with no mesh behind it.
    const placeable = (id: number | null | undefined) =>
      (id != null && modelOf.has(id) ? id : null);
    write({
      kind: s.kind ?? 'combining',
      movingId: placeable(s.suggests?.moves),
      targetId: placeable(s.suggests?.onto),
      // THE TOOL IS THE SUB-STEP'S OWN: Phase 4 lists what this gesture uses — see Slot.tool.
      toolId: s.tool ?? null,
    });
  };

  // THE ANALYSIS'S OWN ANSWER, PUT INTO A SLOT NOBODY HAS ANSWERED. One place, because the
  // arrows and the opening render both want it and they wanted it on slightly different terms.
  //
  // NOT BEFORE THE MESHES ARE KNOWN. `applySuggestion` keeps only the parts it can draw — an
  // id with no model in the Parts DB is a selector value with nothing behind it — and the mesh
  // jobs arrive a moment after the page does. Run in that moment, the suggestion resolved
  // every part to null and wrote THAT into the book, where the guard below then found a slot
  // that had been answered and left it alone forever: a sub-step whose two parts both have
  // models, permanently blank, saying Phase 4 had not named them. It was visible as a flash —
  // the honest sentence for a second, then the wrong one.
  //
  // SO A SLOT IS ALSO RE-OFFERED once when what it holds has no part in it at all, which is
  // how a document already carrying those empty fittings comes back without anyone re-running
  // anything. A fitting somebody has actually filled is never touched.
  //
  // AND ONCE IS ONCE, kept in a ref rather than read back off the book — which is what stops
  // this being the loop it looks like. Where the parts genuinely have no mesh the write
  // changes nothing about the test that provoked it, and every write is a new `bySlot` that
  // wakes the effect again.
  const suggested = useRef(new Set<string>());
  const suggestBlank = (s: Slot) => {
    // Nothing moves there, so nothing is suggested for it (see stillSubSteps).
    //
    // AND NOT BEFORE THE MANUAL'S PARTS ARE RESOLVED TO THEIR CATALOG ROWS, which land apart from the
    // models: suggested in between, a sub-step was given the piece it receives and no part to move,
    // marked as suggested, and left that way — "Phase 4 has not said which part moves" under a
    // sentence where Phase 4 says it.
    if (!jobsLoaded || !partsResolved) return;
    const stored = book.bySlot[s.key];
    // ONCE PER STATE OF WHAT IT CAN BE GIVEN, not once for good: a sub-step opened before the piece
    // it receives was signed off is given the part that moves and no piece, and the piece arriving
    // a minute later is a different suggestion. Kept by sub-step alone, it stayed "Phase 4 has not
    // said which part moves" under a sentence where Phase 4 says it, until the page was reloaded.
    const has = (id: number | null | undefined) => id != null && modelOf.has(id);
    const once = `${s.key}|${has(s.suggests?.onto)}|${has(s.suggests?.moves)}`;
    if (stored && (fittingChosen(stored) || suggested.current.has(once))) return;
    suggested.current.add(once);
    applySuggestion(s);
  };

  // MOVING TO A SLOT SHOWS THAT SLOT'S OWN FITTING, and carries nothing from this one.
  // Two parts chosen for 1B are 1B's answer; arriving at 3A with them still selected would
  // be that sub-step answered with another one's numbers, and answered INVISIBLY — the
  // selectors full, the distances plausible, and nothing saying where they came from.
  //
  // A slot nobody has answered opens FILLED IN from whatever has been said about the movement
  // — its parts ticked, and the selectors set. That is Phase 5's own answer where the button
  // has been pressed on this sub-step before, and Phase 4's old stored clause on a manual
  // analysed back when it still answered this (see assemblySlots). A sub-step nobody has asked
  // about opens empty, which is the honest picture of a question nobody has paid for.
  //
  // ONCE, ON ARRIVAL, AND ONLY WHEN THE SLOT IS UNANSWERED. The model is reading a diagram
  // and the person on this page is looking at the two meshes, so the moment somebody has
  // touched a slot it is theirs — re-suggesting on every visit would undo their correction
  // and do it invisibly. A suggestion that only ever fills a blank cannot overwrite anything;
  // the button is the one thing that may, and it is a press rather than a side effect.
  const goTo = (index: number) => {
    const next = slots[index];
    if (!next) return;
    setAtEnd(false);
    setPlaying(false);
    goToSlot(docId, next.key);
    suggestBlank(next);
  };
  const walk = useArrowSteps<HTMLDivElement>(at, slots.length, goTo);

  // EVERY SUB-STEP OPENS ON ITS FIRST MOMENT: the parts apart, about to move. The scrubber is one
  // state for the whole phase, and was left wherever the last sub-step had it — at its end, once
  // it had been played through, signed off or typed into — so the next opened already assembled.
  useEffect(() => {
    setPlaying(false);
    setT(0);
  }, [slot?.key]);

  // AND THE SLOT THE PHASE OPENS ON, which nobody stepped onto. The suggestion used to be
  // applied by the arrows alone, which was right while it came from a call somebody had paid
  // for: a sub-step nobody had asked about opened empty because nobody had asked. It comes
  // from Phase 4's own answer now — every analysed sub-step has one, free — so an empty
  // viewport under a row of chips saying which part moves is the phase withholding what it
  // already knows.
  //
  // STILL ONLY WHERE THE SLOT IS UNANSWERED — see suggestBlank, which is the whole of that
  // rule and is what the arrows call too.
  useEffect(() => {
    if (slot) suggestBlank(slot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.key, book.bySlot, modelOf, jobsLoaded, partsResolved]);

  // ASK WHAT THIS SUB-STEP IS. Phase 5's own call, about the sub-step on screen and no other.
  //
  // The answer comes back as ids into the Phase 2 parts list, so it is resolved here through
  // the SAME resolver the slots are built with (useFittingResolver) — a fresh answer and the
  // same answer read back after a reload must land on the same two parts, and two resolvers
  // is exactly how they would stop doing so.
  // THE TWO PARTS' BOXES, for a rule's shot to be framed on the action (see movementShot): the
  // model's own, else what the phase measures it at.
  // AND THE TOOL'S, where it rides on the moving part at the placement about to be written (`next`).
  const shotSizes = async (next: Partial<Fitting> = {}) => {
    const boxOf = async (id: number | null | undefined): Promise<Vec3 | null> => {
      const job = id != null ? modelOf.get(id) : null;
      const own = job ? await jobBoxMm(job) : null;
      const d = sizeOf(id);
      return own ?? (d?.x && d.y && d.z ? [d.x, d.y, d.z] : null);
    };
    const ends = (which: 'onto' | 'moves', held: number | null) => {
      const said = slot?.suggests?.[which];
      return said != null && modelOf.has(said) ? said : held;
    };
    const ride = withRide({ ...fitting, ...next }).toolRide;
    const toolJob = toolItemId != null ? modelOf.get(toolItemId) : null;
    const toolSize = ride && toolJob ? await jobBoxMm(toolJob) : null;
    return {
      target: await boxOf(ends('onto', fitting.targetId)),
      moving: await boxOf(ends('moves', fitting.movingId)),
      tool: ride && toolSize ? { size: toolSize, at: ride } : null,
      fit: fitFor(next),
    };
  };

  // ZOOM 1 AS THE VIEWER WILL FIT IT (see AssemblyView.widthAt): the whole sub-step, its tool and its
  // hardware, at the placement the rule is about to write. Nothing where the scene is not drawing
  // those parts — the same test as seenFor — and the shot is then the boxes' estimate.
  const fitFor = (next: Partial<Fitting>): ShotSizes['fit'] => {
    const scene = view.current;
    const to = { ...fitting, ...next };
    if (!scene || state !== 'ready' || to.targetId !== fitting.targetId || to.movingId !== fitting.movingId
      || !!to.mirrored !== !!fitting.mirrored) return null;
    const placed = assemblyOf(withPreps(slot, withRide(to)));
    return (eye, aim) => scene.widthAt(placed, STEP, eye, aim);
  };

  // HOW MUCH OF THE MOVING PART EACH SIDE HIDES, for a rule's shot to be taken from one that shows
  // it (see movementShot) — asked of the meshes on screen, at the placement the rule is about to
  // write. Nothing where they are not the ones it will draw (not loaded yet, or the rule is also
  // choosing the parts): the shot is then the rule's own side, as it was.
  const seenFor = (next: Partial<Fitting>): HiddenFrom | undefined => {
    const scene = view.current;
    const to = { ...fitting, ...next };
    if (!scene || state !== 'ready' || to.targetId !== fitting.targetId || to.movingId !== fitting.movingId
      || !!to.mirrored !== !!fitting.mirrored) return undefined;
    const placed = assemblyOf(to);
    return (eyes, cams) => scene.hiddenFrom(placed, STEP, eyes, cams);
  };

  // WHOSE PLACEMENT A RULE'S IS, and which models it was worked out on — see Fitting.placedBy.
  // AND THE SUB-STEP'S OWN TOOL WITH IT (see Slot.tool): a rule wrote the movement and left the tool
  // as the fitting had it, and a fitting begun before Phase 4 listed tools had none, so the Allen key
  // was missing from 1-11 though its sentence names it.
  const ruleTool = slot?.tool ?? fitting.toolId;
  // THE GESTURE AS PHASE 4 NOW SAYS IT — see Fitting.placedFor.
  const gestureSig = slot ? JSON.stringify([slot.parts.map((p) => [p.itemId, p.role, p.qty]), slot.textEn]) : '';
  const byRule = (targetId: number | null, movingId: number | null) => ({
    placedBy: 'rule' as const,
    shotV: MOVE_SHOT_V,
    placedFor: gestureSig,
    toolId: ruleTool,
    nextToolId: slot?.nextTool ?? fitting.nextToolId ?? null,
    placedOn: marksNow({ targetId, movingId }, modelOf, tools.find((tool) => tool.value === ruleTool)?.itemId ?? null),
  });

  // A FITTING WITH NO TOOL WHERE ITS SUB-STEP HAS ONE is given it, once, where nothing has signed it
  // off (a write withdraws a sign-off). The rule placing it would write it too, but a sub-step it
  // placed before this was kept would never be placed again to get it.
  useEffect(() => {
    // Only once the store stands on this sub-step: a write goes to the one it thinks is open.
    if (IS_LOCKED || leader || !slot?.tool || here !== slot.key || fitting.toolId != null || fitting.validated) return;
    write({ toolId: slot.tool });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.key, slot?.tool, here, fitting.toolId, fitting.validated, leader]);
  // AND ITS SECOND TOOL, the same way (see Fitting.nextToolId).
  useEffect(() => {
    if (IS_LOCKED || leader || !slot?.nextTool || here !== slot.key || fitting.nextToolId != null || fitting.validated) return;
    write({ nextToolId: slot.nextTool });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.key, slot?.nextTool, here, fitting.nextToolId, fitting.validated, leader]);

  const askMovement = async () => {
    // Never from a repeat: the button is off screen there, and this is the half of that which
    // costs money if it is ever wrong.
    if (!slot || !slot.letter || leader) return;
    const key = slot.key;
    // FROM THE SET, and no call: where they end up is the set's, and the way in is the shaft's
    // axis from its tip (see approachFromSet) — measured against the meshes on screen only where
    // the shaft cannot say, so it waits for them.
    if (fromSet) {
      const scene = view.current;
      if (!scene || state !== 'ready') return;
      setSettingFromSet(true);
      setSetError(null);
      try {
        const { axis, travel, seat } = await approachFromSet(scene, fitting, fromSet, STEP);
        if (hereRef.current !== key) return;
        // Not left to be slid home: the set's seat is where they meet, measured, not a box
        // against a box (see `seated`) — a ring stopped under its shaft's head (see ringStop).
        const pose = { ...setPose(fromSet), seat, axis, travel };
        const shot = await movementShot(pose, await shotSizes(pose), seenFor({ ...pose, turn: null }));
        if (hereRef.current !== key) return;
        write({ ...pose, turn: null, copies: undefined, seated: undefined, ...shot, ...byRule(fitting.targetId, fitting.movingId) });
      } catch (e) {
        if (hereRef.current === key) setSetError(sayRuleError(e, tr));
      } finally {
        setSettingFromSet(false);
      }
      return;
    }
    // FROM THE JOINT, and no call either: where it rests is the product's, the way in the line of
    // the hardware that will join the two, as far out as the joint view stands them apart.
    if (fromJoint) {
      setSettingFromSet(true);
      setSetError(null);
      try {
        const travel = await travelFromJoint(fromJoint);
        const seat = await fromJoint.seat(modelOf);
        const pose = { targetSeat: [0, 0, 0] as Vec3, seat, axis: fromJoint.axis, travel };
        // ITS OWN SHOT, across the way in (see movementShot) — over whatever view an older call's
        // reply left it turned to.
        const moved = {
          ...pose, targetSpin: [0, 0, 0] as Vec3, spin: [0, 0, 0] as Vec3, turn: null,
          drive: 'straight' as const, pitch: 0, depth: 0,
          // Which hand of a pair it brings — drawn reflected where it is the other one.
          mirrored: fromJoint.mirrored,
        };
        const shot = await movementShot(pose, await shotSizes(moved), seenFor(moved));
        if (hereRef.current !== key) return;
        write({ ...moved, copies: undefined, seated: undefined, ...shot, ...byRule(fitting.targetId, fitting.movingId) });
      } catch (e) {
        if (hereRef.current === key) setSetError(sayRuleError(e, tr));
      } finally {
        setSettingFromSet(false);
      }
      return;
    }
    // FROM ITS HOLE, and no call either: resting in it, in along it, on the piece the sub-steps
    // before have made.
    if (fromHole) {
      setSettingFromSet(true);
      setSetError(null);
      try {
        const m = await fromHole.place(modelOf);
        const pose = { ...m, targetSeat: [0, 0, 0] as Vec3 };
        // WITH ITS TWO PARTS, as Phase 4 names them: a sub-step opened before the piece it receives
        // was signed off has none chosen, and a movement of nothing is not drawn.
        const drawable = (id: number | null | undefined) => (id != null && modelOf.has(id) ? id : null);
        const targetId = drawable(slot.suggests?.onto) ?? fitting.targetId;
        const movingId = drawable(slot.suggests?.moves) ?? fitting.movingId;
        const moved = { targetId, movingId, ...pose, targetSpin: [0, 0, 0] as Vec3 };
        const shot = await movementShot(pose, await shotSizes(moved), seenFor(moved));
        if (hereRef.current !== key) return;
        // EVERY COPY, where it puts several covers on — and none left over from a run before.
        write({ ...moved, copies: m.copies, seated: undefined, ...shot, ...byRule(targetId, movingId) });
      } catch (e) {
        if (hereRef.current === key) setSetError(sayRuleError(e, tr));
      } finally {
        setSettingFromSet(false);
      }
      return;
    }
  };

  // THE HARDWARE PREPARED ON THE MOVING PART IN THE HAND, PLACED ON ARRIVAL (see Fitting.prepOn):
  // the washer Phase 4 lists with this gesture, put where the hardware set built it on the screw
  // and brought in along the screw from its tip, as a sub-step of its own used to be (see
  // approachFromSet). Once per moving part; not under a sign-off. Where the set cannot say, why.
  // OR PUT INTO THE MOVING PART, where that is a component: the barrel nuts into the side rail,
  // each in the hole Find holes gave it (see useHolePlacement), turned into line on the way in.
  const prepared = slot?.parts.find((p) => p.role === 'prepared' && !p.tool)?.itemId ?? null;
  const prepHole = useHolePlacement(docId, slot?.key, 'prepared');
  const [prepWhy, setPrepWhy] = useSlotNote(slot?.key);
  // HARDWARE STOOD IN ITS HOLES BY THE HOLE RULE, each unit, written as `field` (see PrepOn).
  const placeByHole = (
    hole: HolePlacement, itemId: number, onto: number, field: 'prepOn' | 'nextOn' | 'lastOn', why: (w: string) => void,
  ) => {
    const key = slot?.key;
    void hole.place(modelOf)
      .then((m) => {
        if (hereRef.current !== key) return;
        const more = (m.copies ?? []).map((c) => ({ ...c, turn: m.turn ? { ...m.turn, axis: c.axis } : null }));
        write({
          [field]: {
            itemId, onto, seat: m.seat, spin: m.spin, axis: m.axis, travel: m.travel, turn: m.turn, more, v: PREP_BY_HOLE,
            ...(m.pitch > 0 && m.depth > 0 ? { pitch: m.pitch, depth: m.depth } : {}),
          },
          validated: false,
        });
      })
      .catch((e) => { if (hereRef.current === key) why(sayRuleError(e, tr)); });
  };
  // The second hardware, where the gesture has one (see Fitting.nextOn).
  const second = slot?.parts.filter((p) => p.role === 'added' && !p.tool)[1]?.itemId ?? null;
  const prepAsked = useRef('');
  useEffect(() => {
    if (IS_LOCKED || leader || !slot || prepared == null || fitting.movingId == null || fitting.validated) return;
    if (!jobsLoaded || !setsKnown || state !== 'ready' || !view.current) return;
    const had = prepOf(fitting);
    if (had?.itemId === prepared && (!prepHole || isDeclined(prepHole) || had.v === PREP_BY_HOLE)) return;
    if (!prepHole && fitting.nextPrep?.itemId === prepared && fitting.nextPrep.onto === second) return;
    // Asked again once the hole can answer: its holes may land after the part did.
    const by = prepHole ? (isDeclined(prepHole) ? 'no hole' : 'hole') : 'set';
    const asked = `${slot.key}|${fitting.movingId}|${second}|${prepared}|${by}`;
    if (prepAsked.current === asked) return;
    prepAsked.current = asked;
    setPrepWhy(null);
    const onto = fitting.movingId;
    const key = slot.key;
    if (prepHole) {
      if (isDeclined(prepHole)) setPrepWhy(sayDeclined(prepHole, tr));
      else placeByHole(prepHole, prepared, onto, 'prepOn', setPrepWhy);
      return;
    }
    const placed = (on: number) => setPlacement(PREPARE_KIND, modelOf.get(prepared), modelOf.get(on), manualSets,
      { moving: labelOf(prepared), target: labelOf(on) });
    // ON THE MOVING PART, OR ELSE ON THE SECOND HARDWARE where its set holds that one instead (see
    // Fitting.nextPrep): the spring washer of 2-2 goes on the JCBC screws, not on the barrel nut.
    const onMoving = placed(onto);
    const onSecond = second != null && (!onMoving || isDeclined(onMoving)) ? placed(second) : null;
    const [p, on, field] = onSecond && !isDeclined(onSecond)
      ? [onSecond, second!, 'nextPrep' as const] : [onMoving, onto, 'prepOn' as const];
    if (!p || isDeclined(p)) {
      setPrepWhy(p ? sayDeclined(p, tr) : null);
      return;
    }
    void approachFromSet(view.current, { ...fitting, targetId: on, movingId: prepared }, p, STEP)
      .then(({ axis, travel, seat }) => {
        if (hereRef.current !== key) return;
        write({
          [field]: { itemId: prepared, onto: on, seat, axis, travel },
          // One place or the other, never both.
          ...(field === 'nextPrep' && had?.itemId === prepared ? { prepOn: null } : {}),
          validated: false,
        });
      })
      .catch((e) => { if (hereRef.current === key) setPrepWhy(sayRuleError(e, tr)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.key, prepared, second, prepHole, fitting.movingId, fitting.prepOn, fitting.nextPrep, fitting.validated, jobsLoaded, setsKnown, state, leader, modelOf]);

  // THE SECOND HARDWARE PUT INTO THE SAME PART, PLACED ON ARRIVAL (see Fitting.nextOn): the wood
  // dowels into the rail end, after its barrel nut — by their holes. Once per part fitted onto;
  // not under a sign-off, never from a repeat.
  const nextHole = useHolePlacement(docId, slot?.key, 'next');
  const [nextWhy, setNextWhy] = useSlotNote(slot?.key);
  const nextAsked = useRef('');
  useEffect(() => {
    if (IS_LOCKED || leader || !slot || second == null || !nextHole || fitting.targetId == null || fitting.validated) return;
    if (!jobsLoaded || state !== 'ready') return;
    const had = nextOf(fitting);
    if (had?.itemId === second && had.v === PREP_BY_HOLE) return;
    const asked = `${slot.key}|${fitting.targetId}|${second}|${isDeclined(nextHole)}`;
    if (nextAsked.current === asked) return;
    nextAsked.current = asked;
    setNextWhy(null);
    if (isDeclined(nextHole)) setNextWhy(sayDeclined(nextHole, tr));
    else placeByHole(nextHole, second, fitting.targetId, 'nextOn', setNextWhy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.key, second, nextHole, fitting.targetId, fitting.nextOn, fitting.validated, jobsLoaded, state, leader]);

  // AND THE THIRD, after it (see Fitting.lastOn): the wood button over the screw just driven into its
  // barrel nut. The same way, once the second stands.
  const lastHole = useHolePlacement(docId, slot?.key, 'last');
  const third = slot?.parts.filter((p) => p.role === 'added' && !p.tool)[2]?.itemId ?? null;
  const [lastWhy, setLastWhy] = useSlotNote(slot?.key);
  const lastAsked = useRef('');
  useEffect(() => {
    if (IS_LOCKED || leader || !slot || third == null || !lastHole || fitting.targetId == null || fitting.validated) return;
    if (!jobsLoaded || state !== 'ready' || !nextOf(fitting)) return;
    const had = lastOf(fitting);
    if (had?.itemId === third && had.v === PREP_BY_HOLE) return;
    const asked = `${slot.key}|${fitting.targetId}|${third}|${isDeclined(lastHole)}`;
    if (lastAsked.current === asked) return;
    lastAsked.current = asked;
    setLastWhy(null);
    if (isDeclined(lastHole)) setLastWhy(sayDeclined(lastHole, tr));
    else placeByHole(lastHole, third, fitting.targetId, 'lastOn', setLastWhy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.key, third, lastHole, fitting.targetId, fitting.nextOn, fitting.lastOn, fitting.validated, jobsLoaded, state, leader]);

  // AND THEIR OWN SHOTS, the hardware prepared into the moving part and the second hardware (see
  // PrepOn.shot): worked out once their places are, on the meshes on screen. Not under a sign-off,
  // never from a repeat.
  const shotAsked = useRef('');
  // THE SIZES A PREPARATION'S SHOT IS FRAMED BY: the two parts, the hardware, the tool.
  const prepSizes = async (itemId: number) => {
    const box = async (id: number | null | undefined) => {
      const job = id != null ? modelOf.get(id) : null;
      return job ? jobBoxMm(job) : null;
    };
    const { target, moving } = await shotSizes();
    const tool2 = tools.find((tool) => tool.value === fitting.nextToolId)?.itemId ?? null;
    return { target, moving, prep: await box(itemId), tool: await box(toolItemId), tool2: await box(tool2), fit: fitFor({}) };
  };
  // THE VIEWS EACH OF THIS SUB-STEP'S SHOTS WEIGHS, for the pictures under the transport (see
  // ShotCandidates): the movement's as its rule would now take it, the preparation's and the
  // second hardware's as they were taken.
  const candidateRows = (): CandidateRow[] => {
    const scene = view.current;
    if (!scene || !slot || leader) return [];
    const played = withRide(fitting);
    const hiddenOf = (asm: Assembly): HiddenFrom => (eyes, cams) => scene.hiddenFrom(asm, STEP, eyes, cams);
    const lookOf = (c: ShotCandidate) => shotAt(c.cam, 0, played.mirrorAll);
    // IN THE ORDER THEY PLAY: the preparation, the movement, the second hardware's preparation, the
    // second hardware (see camAt). EVERY PART OF THE GESTURE, BUT VIEWS ONLY FOR THE SHOTS PLAYED: a
    // hardware's own where it has one, as camAt plays it, on the first part it covers. A part played
    // in another's shot is its title alone: a washer slid onto its screw in the hand (no hole places
    // it) showed a "chosen" view the clip never used, the movement's being on screen all along.
    const hardware = (which: 'prep' | 'next'): CandidateRow[] => {
      const on = which === 'prep' ? prepOf(fitting) : nextOf(fitting);
      if (!on) return [];
      const ownShot = which === 'prep' ? !!prepHole && !!on.shot && prepTurns(fitting) > 0 : !!on.shot;
      const row = (title: string, withViews: boolean): CandidateRow => ({
        kind: which, title, at: prepShotAt(played, which), lookOf, measured: labelOf(on.itemId),
        ...(withViews ? { compute: async () => prepShotCandidates(played, which, await prepSizes(on.itemId), hiddenOf) } : {}),
      });
      const title = tr(`notice.assembly.candidates.${which}`);
      // The second hardware's shot holds through its own preparation (see Fitting.nextPrep), and is
      // the second hardware's: its views go on that one's row, the washer's is its title.
      return which === 'next' && nextPrepOf(fitting)
        ? [row(tr('notice.assembly.candidates.nextPrep'), false), row(title, ownShot)]
        : [row(title, ownShot)];
    };
    return [
      ...hardware('prep'),
      ...(fittingChosen(fitting) ? [{
        kind: 'movement' as const, title: tr('notice.assembly.candidates.movement'), at: movementMid(played), lookOf,
        measured: labelOf(fitting.movingId),
        compute: async () => shotCandidates(fitting, await shotSizes(), seenFor({})),
      }] : []),
      ...hardware('next'),
      // AND THE THIRD, under the second's shot: its title alone (see Fitting.lastOn).
      ...(lastOf(fitting) ? [{
        kind: 'next' as const, title: tr('notice.assembly.candidates.last'), at: 1, lookOf, measured: labelOf(lastOf(fitting)!.itemId),
      }] : []),
    ];
  };
  useEffect(() => {
    if (IS_LOCKED || leader || !slot || fitting.validated) return;
    if (state !== 'ready' || !view.current || hereRef.current !== slot.key) return;
    const which = ([['prep', prepHole && prepOf(fitting)], ['next', nextOf(fitting)]] as const)
      .find(([, on]) => on && !shotDone(on, fitting));
    if (!which) return;
    const [kind] = which;
    const on = which[1]!;
    const field = kind === 'prep' ? 'prepOn' : 'nextOn';
    const asked = `${slot.key}|${kind}|${JSON.stringify(on)}`;
    if (shotAsked.current === asked) return;
    shotAsked.current = asked;
    const key = slot.key;
    const scene = view.current;
    const why = kind === 'prep' ? setPrepWhy : setNextWhy;
    void (async () => {
      const shot = await prepShot(withRide(fitting), kind, await prepSizes(on.itemId),
        (asm) => (eyes, cams) => scene.hiddenFrom(asm, STEP, eyes, cams));
      if (hereRef.current !== key) return;
      if (shot) write({ [field]: { ...on, shot, shotV: PREP_SHOT_V, shotFor: shotFor(fitting) } });
      else why(tr('notice.assembly.noShot'));
    })().catch((e) => {
      // Measured on a scene rebuilt under it: asked again once the new one is ready.
      if (sceneClosed(e)) shotAsked.current = '';
      else if (hereRef.current === key) why(sayRuleError(e, tr));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.key, fitting.prepOn, fitting.nextOn, fitting.validated, state, leader, prepHole, toolItemId, shotFor(fitting)]);

  // A SUB-STEP ITS RULE CAN PLACE IS PLACED ON ARRIVAL, without the badge's row being pressed.
  //
  // The press asked nothing and cost nothing — the joint, the hole or the set already says where
  // the part goes — so it was a click per sub-step to be shown what the page already knew, sixty
  // times down a manual. The row is still there, and is now the way BACK to the rule.
  //
  // OVER NOTHING, OVER AN ANSWER, AND OVER ITS OWN EARLIER WORK ON OTHER MODELS — never over a
  // hand's, and never under a sign-off (see Fitting.placedBy). The third is what follows a
  // change upstream: the piece this stands on was made another way, the sign-off here was
  // withdrawn for it (SignOffSync), and the rule's old answer is about a piece that is gone.
  //
  // ONCE PER STATE OF ITS PARTS, the last one asked on kept per sub-step: a rule that fails says
  // why on the page, and asking it again on every render would say it for ever.
  const autoPlaced = useRef(new Map<string, string>());
  // Whether a rule places this sub-step at all, and whether it has what it stands on: the set,
  // the meshes on screen (which it waits for); the joint, its two parts chosen; the hole, the
  // piece the sub-steps before have made and the hardware that goes in it.
  const ruled = !IS_LOCKED && !leader && !!slot?.letter
    && !!(fromSet || fromJoint || fromHole) && !fitting.validated;
  const has = (id: number | null | undefined) => id != null && modelOf.has(id);
  const stoodOn = !!fromSet || fittingChosen(fitting)
    || (!!fromHole && has(slot?.suggests?.onto) && has(slot?.suggests?.moves));
  // THE RULE IS PLACING IT, OR IS ABOUT TO — and until it has, what the viewport holds is not
  // this sub-step: its two parts where the last answer left them, or wherever parts with no
  // pose are stood. A picture of something else, at full strength, under the name of this one.
  // So the wait is drawn over it WHOLE (see the viewport), unlike a call's, which is drawn
  // through: there the picture is this sub-step's earlier answer and worth looking at.
  // Not once the rule has said why it cannot, and not where it has nothing to stand on —
  // both are a sub-step waiting for somebody, not for the page.
  const placingByRule = settingFromSet
    || (ruled && stoodOn && awaitsPlacing(fitting) && !setError && state !== 'failed');
  // AND UNTIL EVERYTHING ELSE IT WORKS OUT ON ARRIVAL IS DONE: the hardware put into the moving part
  // and the second hardware, by their holes, and their shots. Each landed on screen as it came — the
  // picture, then rebuilt with the nuts, then the camera jumping to their shot. Only what a hole
  // places, which says why where it cannot, so the wait always ends: in the picture or a reason.
  const working = !IS_LOCKED && !leader && !!slot && !fitting.validated && state !== 'failed';
  const placedByHole = (on: PrepOn | null, itemId: number | null) => on?.itemId === itemId && on.v === PREP_BY_HOLE;
  const settling = placingByRule || (working && (
    (!!prepHole && !isDeclined(prepHole) && !prepWhy && fitting.movingId != null
      && (!placedByHole(prepOf(fitting), prepared) || !shotDone(prepOf(fitting), fitting)))
    || (!!nextHole && !isDeclined(nextHole) && !nextWhy && fitting.targetId != null
      && (!placedByHole(nextOf(fitting), second) || !shotDone(nextOf(fitting), fitting)))
  ));
  // WHAT A WAIT IS ON, said under the spinner and logged at every change: 1-3 spun for minutes in
  // the tab its sub-steps before had just been signed off in, and in no fresh one, and nothing on
  // the page said which of the things above it was waiting for. Each is one of the conditions above.
  const waitingOn = (settling || state === 'loading') ? [
    state === 'loading' && 'the meshes (scene loading)',
    settingFromSet && 'the set placing it',
    placingByRule && !settingFromSet && [
      'the rule placing it',
      !jobsLoaded && 'models not loaded',
      !partsResolved && 'parts not resolved',
      !setsKnown && 'hardware sets not read',
      fromSet && state !== 'ready' && `scene ${state}`,
      autoPlaced.current.get(slot?.key ?? '') === `${signedNow.target}|${signedNow.moving}|${gestureSig}` && 'asked already, no answer',
    ].filter(Boolean).join(', '),
    working && !!prepHole && !isDeclined(prepHole) && !prepWhy && fitting.movingId != null
      && !placedByHole(prepOf(fitting), prepared) && 'the prepared hardware by its holes',
    working && !!prepHole && placedByHole(prepOf(fitting), prepared) && !shotDone(prepOf(fitting), fitting) && 'the prepared hardware\'s shot',
    working && !!nextHole && !isDeclined(nextHole) && !nextWhy && fitting.targetId != null
      && !placedByHole(nextOf(fitting), second) && 'the second hardware by its holes',
    working && !!nextHole && placedByHole(nextOf(fitting), second) && !shotDone(nextOf(fitting), fitting) && 'the second hardware\'s shot',
  ].filter(Boolean).join('; ') : '';
  // SAID IN THE CONSOLE AND TO THE SERVER'S LOG, which outlives the tab (see post_doc_waitlog). A
  // beacon: nothing waits on it, and it is not a write the shared reads must forget their answers
  // for (see sharedGet). Not from the demo, which refuses every write.
  const sayWait = (key: string, line: string) => {
    console.info(`[assembly] ${key} ${line}`);
    if (!IS_DEMO) {
      navigator.sendBeacon?.(apiUrl(`/api/notice/doc/${docId}/waitlog`),
        new Blob([JSON.stringify({ slot: key, line })], { type: 'application/json' }));
    }
  };
  // Since when this sub-step has been waiting, for the log's timings.
  const waitSince = useRef<{ key: string; at: number } | null>(null);
  useEffect(() => {
    const key = slot?.key ?? '';
    const say = (line: string) => sayWait(key, line);
    const now = performance.now();
    if (waitSince.current?.key !== key) waitSince.current = waitingOn ? { key, at: now } : null;
    const since = waitSince.current ? `${((now - waitSince.current.at) / 1000).toFixed(1)} s` : '';
    if (waitingOn) {
      waitSince.current ??= { key, at: now };
      say(`waiting on: ${waitingOn}${since ? ` (at ${since})` : ''}`);
    } else if (waitSince.current) {
      say(`done waiting after ${since}`);
      waitSince.current = null;
    }
  }, [waitingOn, slot?.key, docId]);
  useEffect(() => {
    // AND NOT BEFORE THE MANUAL'S SETS ARE READ: the tool's ride and the shot framing it come from
    // them, and 1-1's shot was worked out without its screwdriver, x3.18 where it is x1.67.
    if (!ruled || !slot || !jobsLoaded || !partsResolved || !setsKnown) return;
    if (settingFromSet) return;
    if (!stoodOn || (fromSet && state !== 'ready')) return;
    // ON OTHER MODELS, OR FOR ANOTHER GESTURE: Phase 4 re-run since, saying two screws where it
    // said one.
    // OR BY AN OLDER WORKING OF ITS SHOT (see MOVE_SHOT_V): placed again, which works it out again.
    const moved = fitting.placedBy === 'rule'
      && (changedSince(fitting.placedOn, fitting, modelOf, null).length > 0 || fitting.placedFor !== gestureSig
        || fitting.shotV !== MOVE_SHOT_V);
    if (!moved && !awaitsPlacing(fitting)) return;
    const on = `${signedNow.target}|${signedNow.moving}|${gestureSig}`;
    if (autoPlaced.current.get(slot.key) === on) return;
    autoPlaced.current.set(slot.key, on);
    void askMovement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot?.key, fromSet, fromJoint, fromHole, jobsLoaded, partsResolved, state, modelOf,
    fitting.validated, fitting.placedBy, fitting.placedOn, fitting.placedFor, fitting.shotV, fitting.targetId, fitting.movingId,
    signedNow, settingFromSet, leader, gestureSig, setsKnown]);

  const toggleAllowed = (itemId: number) => {
    const excluded = ticked.has(itemId)
      ? [...fitting.excluded, itemId]
      : fitting.excluded.filter((id) => id !== itemId);
    setPlaying(false);
    write({
      excluded,
      // Unticking the part a dropdown is showing has to take it out of the dropdown too,
      // or the fitting keeps a part the slot says is not in it.
      movingId: excluded.includes(fitting.movingId ?? -1) ? null : fitting.movingId,
      targetId: excluded.includes(fitting.targetId ?? -1) ? null : fitting.targetId,
    });
  };

  // AND A TOOL OFF THE ROW, or back onto it. The same list as the parts' ticks: `excluded`
  // is which chips are switched off, and a tool's catalogue row cannot collide with a part's.
  // It reaches neither dropdown of parts — `ticked` is computed over `slotItems`, which has
  // no tools in it — so what it changes is the chip and, where it names the tool the selector
  // is showing, that selection.
  //
  // The two are matched THROUGH the tool list, which is the only thing that knows both names
  // for a tool: the chip carries a catalogue item id and the selector stores the tool's index
  // in the Phase 2 parts list (see Fitting.toolId, which says why it is an index).
  const toggleTool = (itemId: number) => {
    const off = fitting.excluded.includes(itemId);
    const picked = tools.find((x) => x.value === fitting.toolId);
    write({
      excluded: off
        ? fitting.excluded.filter((id) => id !== itemId)
        : [...fitting.excluded, itemId],
      ...(!off && picked?.itemId === itemId ? { toolId: null } : {}),
    });
  };

  // ONE BOX PER READING, ALL THREE THE SAME BOX. What kind of movement this is, what the
  // manual says to do, and which parts it uses: three answers about the sub-step on screen,
  // and they were a chip, a paragraph and a loose row of buttons — one of them in a border,
  // the other two not, and only the middle one named. Which of them is a control was
  // something you found out by clicking.
  // ONE BOX, FIVE READINGS, RULED APART. They were five boxes stacked with air between them,
  // which draws four borders and four gaps to say what one border and four rules say better:
  // these are the readings of ONE sub-step, and a reader scanning down them was crossing a
  // frame every time. A stack of framed cards reads as a stack of unrelated things.
  //
  // THE ORDER IS THE SENTENCE THE ROW MAKES: what kind of gesture this is, what the manual
  // says to do, how many times it is done, what it takes in hand, and what it leaves. The same
  // order Phase 4's table reads left to right, so the two pages describe a gesture the same
  // way round.
  // The card the description stands on, with the numbers under it or alone.
  const END_KEY = '__end';
  const ALL_KEY = '__all';
  const DESCRIBE_CARD = 'mx-auto flex w-full flex-col rounded-xl border border-slate-200 bg-white shadow-sm';
  const panelBox =
    // FOUR COLUMNS ON A WIDE SCREEN rather than four rows: what kind of gesture, what it
    // says, what it takes, what it leaves are short answers each, and stacked they spent the
    // card's whole height saying four brief things one under another — a shape built for
    // paragraphs, given to chips and a sentence a line or two long. Side by side they read
    // as the single sentence their order already makes. Stacked again below `md`, where four
    // columns would be four narrow ones.
    //
    // NO CARD OF ITS OWN: this and the position tables under it are one card now (see the
    // wrapper below), so the chrome lives there and the gap between two boxes is gone. It
    // keeps `max-w-4xl` and is centred in that wrapper — four short columns stretched to the
    // width of the tables would be four columns of mostly nothing.
    // FRAMED, its outer edge drawn as its inner ones are: four columns divided inside and open
    // outside read as a table missing its border.
    // AND WITH WHITE ABOVE AND BELOW IT, not only beside: framed, it sat hard against the card.
    'mx-auto my-3 flex w-full max-w-4xl flex-col divide-y divide-slate-200 rounded-lg border border-slate-200 md:flex-row md:divide-x md:divide-y-0';
  // Each cell's content in its middle, both ways: a short chip beside a two-line sentence sat at
  // the top of a cell twice its height.
  const panelSection = 'flex w-full flex-1 flex-col items-center justify-center gap-1.5 px-4 py-2.5 text-center';
  const panelTitle = 'text-[11px] uppercase tracking-wide text-slate-500';

  // THE PICKED PART'S NUMBERS, handed to the card that names it (see ScenePartCards) rather
  // than placed on the viewport by this file. Built here because it is this file that holds
  // the fitting they are written into.
  // Absent on a repeat: the panel is where a part's place is TYPED, and this page's parts
  // stand where another sub-step put them.
  const coordsPanel = picked && !leader ? (
    <PartCoordsPanel
  pick={{
    instanceId: picked.instanceId,
    centre: shownAt(picked.instanceId),
    angles: spinAt(picked.instanceId),
  }}
  onValidate={(to, turnedTo) => {
    // TYPED AS A PLACE AND STORED AS ONE. A number typed into the panel IS the
    // seat — no arithmetic against where the part happens to be at this moment,
    // which is what made the same typed number mean different things at
    // different points on the scrubber.
    //
    // WHICH IS WHY THE SCRUBBER GOES TO THE END. The boxes READ live now, so
    // typing at t=0.4 would have set the seat and then immediately shown the
    // part's position 0.4 of the way towards it — the typed number replaced by
    // another one, looking for all the world like it had been ignored. At the
    // end, live and seated are the same pose, and what was typed is what is on
    // screen. It is also the only frame in which the answer can be checked.
    //
    // The moving part is the one conversion left, and it is the model's own:
    // its seat is written in the fixed part's frame, so a place in the
    // assembly's frame is that place minus where the fixed part stands.
    const base = fitting.targetSeat;
    held.current = true;
    setPlaying(false);
    setT(1);
    // THE ORIENTATION NEEDS NO SUCH CONVERSION. Every spin is stored in the
    // assembly's frame — the frame the panel shows and the gizmo draws — and
    // assemblyOf is the one place that expresses the moving part's in the
    // fixed part's. So what was typed is what is stored.
    write(picked.instanceId === 'target'
      ? { targetSeat: to, targetSpin: turnedTo }
      : isTool(picked.instanceId)
        ? { toolSeat: to, toolSpin: turnedTo }
        : copyOf(picked.instanceId)
          // A COPY'S OWN PLACE, and nobody else's.
          ? { copies: (fitting.copies ?? []).map((c, k) => (copyId(k + 1) === picked.instanceId
            ? { ...c, seat: seatIn(base, to), spin: turnedTo } : c)) }
          : {
            seat: seatIn(base, to),
            spin: turnedTo,
          });
    // AND THE PANEL STAYS UP. It used to close here, back when the only way to
    // get to this line was pressing "Move here" — one deliberate act, after
    // which the boxes had nothing left to ask. Committing on blur makes that
    // reading wrong: tabbing from X to Y is a commit, and a panel that closed
    // on it would vanish between two fields of the same answer.
  }}
  onClose={() => setPicked(null)}
    />
  ) : undefined;

  // WHAT THIS GESTURE IS — kind, the manual's sentence, what it takes, what it leaves. Built
  // once and drawn in either state of the page: under the picture where there is one, and on
  // its own where there is nothing to draw yet, which is exactly when somebody needs to read
  // what the sub-step is to see why.
  const description = (
    <div className={panelBox}>
    {/* WHAT KIND OF MOVEMENT THIS IS, in a box of its own — and UNDER the sentence it
        is a reading of, which is the order the four boxes are read in: what repeats, what
        the manual says, what the app made of it, what it takes in hand. The app's own
        answers come after the manual's words rather than over them.

        It used to sit inside the sentence's box, on the reasoning that it is a fact ABOUT
        that sentence. So is every other reading on this page: the parts are read out of
        it too, and the fitting under it is what the sentence describes. What the box
        actually held was the manual's own words with one of the app's answers floating
        over them, unlabelled — and an unlabelled chip among labelled blocks is the one
        thing on screen you have to already know to read.

        Not every sub-step joins two parts — "lay the backrest frame (A) flat" moves one
        and joins it to nothing — and the two selectors below ask the same question of
        both, which reads as a form half-filled rather than as a sub-step of another kind.
        The word is the analysis's own.

        WHICH OF THE FIVE, in exactly the wording the sub-step table uses (see
        subStepKindWords). The chip said "PLACING" alone, which was the whole of the
        vocabulary when there were two answers and is now the first word of one: a reader
        who has just come from the table, where the row reads "Placing a single part",
        should not have to work out that this is the same gesture. The sentence is absent on
        a sub-step analysed before the five were asked for, and then the family stands by
        itself — read off the movement's own ids, which is all there is to go on and is not
        nothing.

        WHILE THE CALL IS OUT, the chip spins instead of asserting. Which kind this is is
        the first thing the answer changes, so leaving "PLACING" sitting there is the
        phase stating as settled the very thing it has gone to ask about — and if the
        answer comes back "combining", the word was wrong for the whole wait.

        Neutral grey while it spins: the two colours ARE the two families, so keeping
        either one would be answering in colour what the word has stopped claiming. */}
    {slot?.kind && (
      <div className={panelSection}>
        <span className={panelTitle}>{tr('notice.steps.colKind')}</span>
        <span
          className={`inline-flex items-center justify-center rounded-lg text-center px-2 py-1 text-[11px] font-semibold leading-snug ${
            !kindWords
              ? 'bg-slate-100 text-slate-400'
              : subStepKindTone(kindWords.family)
          }`}
        >
          {kindWords?.text}
        </span>
      </div>
    )}

    {/* WHAT THE SUB-STEP SAYS TO DO. "1A" names a position and says nothing; the line
        under it — "insert the barrel nut (G) into the horizontal hole on the inner face"
        — IS the fitting this phase is being asked to describe, naming the two parts and
        which way one goes into the other. Reading it off the diagram instead was the
        work Phase 4 already did.

        `text-balance` evens the rows out rather than filling each line and leaving the
        last one short. Two sentences of instruction set as one long line and one word is
        the shape that reads as a layout mistake, and the browser can do the line-breaking
        arithmetic better than a max-width guess can. */}
    {slotText && (
      <div className={panelSection}>
        <span className={panelTitle}>{tr('notice.steps.instructions')}</span>
        <p className="text-balance text-center text-sm leading-snug text-slate-600">
          {slotText}
        </p>
      </div>
    )}

    {/* WHAT REPEATED USED TO BE SAID HERE — "Cycle: (×2)" over the sentence, because this
        phase describes ONE movement and a gesture standing for four passes is a different
        thing to judge. A gesture is one gesture now; there are no passes to count. */}

    {/* THE PARTS THAT SUB-STEP USES, ticked or not. Ticking is what the two dropdowns
        below may then offer — the point of standing on a sub-step is that the answer is
        six parts rather than the whole manual.

        A part with no model in the Parts DB is drawn and NOT clickable, rather than
        left out. It is in the sub-step, it simply cannot be placed yet, and a chip
        missing without explanation is a question about this phase; a greyed one is an
        answer about Phase 2. */}
    {slot && (
      <div className={panelSection}>
        {/* NAMED, like the two boxes above it. A row of chips floating under a paragraph
            is a control nobody presses: what it does — tick a part into the dropdowns
            below — is written nowhere, and unlabelled under the manual's own sentence it
            read as decoration on that sentence. */}
        <span className={panelTitle}>{tr('notice.steps.partsTools')}</span>
        {slot.parts.length ? (
          <div className="flex flex-wrap justify-center gap-2">
            {slot.parts.map((p, i) => {
              // A TOOL TICKS LIKE A PART, and starts ticked like one. It is not ticked
              // INTO the two selectors under this — they ask which part moves and what
              // receives it, and a screwdriver is neither — but this row is the list of
              // what the gesture needs in hand, and a tool the analysis named wrongly is
              // exactly as worth taking off it as a part is. Switching one off clears the
              // tool selector when that is the tool it was showing, which is the same rule
              // the parts follow. It keeps the wrench either way: what it is does not
              // change with whether it is wanted.
              //
              // A tool the analysis named without matching it to a catalogue row has no
              // id to record an exclusion against. Drawn ticked and inert: it is needed,
              // and there is nowhere to write down that it is not.
              if (p.tool) {
                const has = p.itemId != null;
                const on = !has || !fitting.excluded.includes(p.itemId as number);
                return (
                  <button
                    key={`t-${p.itemId ?? 'x'}-${i}`}
                    type="button"
                    disabled={!has || !!leader}
                    onClick={() => has && toggleTool(p.itemId as number)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                      on ? partTone : partChipOff
                    } ${has ? '' : 'cursor-default'}`}
                  >
                    <Wrench className="h-3 w-3 shrink-0 opacity-70" />
                    {p.label}
                  </button>
                );
              }
              const usable = p.itemId != null && modelOf.has(p.itemId);
              const on = p.itemId != null && ticked.has(p.itemId);
              return (
                <button
                  // By position: two parts of one sub-step can carry the same printed
                  // name, and a key that collides drops one of them from the row.
                  key={`${p.itemId ?? 'x'}-${i}`}
                  type="button"
                  disabled={!usable || !!leader}
                  onClick={() => usable && toggleAllowed(p.itemId as number)}
                  title={usable ? undefined : tr('notice.assembly.noModel')}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    !usable
                      ? 'cursor-default border-dashed border-slate-300 text-slate-400'
                      : on ? partTone : partChipOff
                  }`}
                >
                  {p.qty != null ? <CountedLabel label={p.label} n={p.qty} /> : p.label}
                  {/* WHICH END OF THE FITTING, drawn exactly as Phase 4's table draws it
                      (see SubStepChips): after the count, in the chip's own ink. It is
                      the answer this page used to ask for in a dropdown, and it has to be
                      READABLE here — with the selectors gone, this row is the only place
                      the phase says which part moves. */}
                  {p.role && <span className="ml-1">{tr(`notice.steps.role.${p.role}`)}</span>}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-slate-400">
            {tr('notice.assembly.noSlotParts')}
          </p>
        )}
      </div>
    )}
    {/* AND WHAT IT LEAVES, last, in the violet the piece wears everywhere else. Phase 4's
        table has said this in a column of its own for a while and this page did not say it
        at all: the phase that ANIMATES a gesture was the one screen where you could not
        read what the gesture produces. It is the same object as the chip a later sub-step
        picks up, drawn the same way, which is the whole argument for the colour.

        EVERY kind leaves a piece, a placing included — a rail held against a frame is one
        object, and the gesture after it works on that object. So the section is absent
        only where nobody named one, which is a gap in the analysis rather than a question
        the kind rules out. */}
    {slot?.output && (
      <div className={panelSection}>
        <span className={panelTitle}>{tr('notice.steps.colOutput')}</span>
        <span className={outputChip}>
          {`${slot.output.id} - ${subStepOutputName(slot.output, language)}`}
          {/* ×1, ALWAYS. A combining leaves one piece, and this page is one pass of the
              cycle: the chips beside it are per-iteration quantities and the animation
              plays a single iteration, so a 4 here would be the only number on the page
              counting something else. The banner above says how many passes there are. */}
          <span className="ml-1 font-semibold tabular-nums">×1</span>
        </span>
      </div>
    )}
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {/* WHERE THE PHASE IS STANDING, and it is the manual's own sequence: step by
          sub-step. Phase 4 already broke every step into
          sub-steps and said which parts each one uses, and this phase was asking a person to
          find those parts again in a list of everything. */}
      <div className="flex flex-col items-center gap-3">
        {/* THE ONE CALL THIS PHASE MAKES, over the sub-step it is about. Above the navigator
            and not down among the selectors it fills: what it answers is WHICH SUB-STEP THIS
            IS — a fitting or a placing, and of what — so it belongs with the sub-step's name
            and its sentence, which are the two things it reads.

            ONE SUB-STEP, the one on screen. Absent on a whole-step slot. */}
        {/* NOT IN THE DEMO AT ALL: a demo shows what was read, not a way to read it again. */}
        {/* OVER THE SUBPHASE SELECTOR, as every phase's calls are (ASSEMBLY_CALL_SLOT_ID, in
            AssemblySubphases) — for the sub-step on screen. */}
        {slot && slot.letter && !leader && !IS_DEMO && callSlot && createPortal(
          <>
            {/* A NAMED BADGE LIKE EVERY OTHER CALL'S, with Run in its menu — it was a big
                button with a badge hung off its edge, two controls for one call. Its Run row is
                in a portal outside any <fieldset>, so the badge checks IS_LOCKED itself.

                A SET PAIR ASKS NOTHING, and neither does a part placed as its joint has it (see
                fromJoint), so its menu has the placing and no prompt: a prompt
                shown for a press that does not send one would describe a call nobody makes.
                What it has instead is how the placing works, and what it came to. */}
            {fromSet || fromJoint || fromHole ? (
              <CallBadge
                key={slot.key}
                named
                // AND WHOSE PLACEMENT IS ON SCREEN, in the badge's own name: the rule's as it worked
                // it out, a hand's since, or nobody's yet. Nothing for one placed before that was
                // kept — there is no telling which it was.
                title={[
                  tr('notice.assembly.badgeMovement'),
                  fitting.placedBy === 'rule' ? tr('notice.assembly.placedByRule')
                    : fitting.placedBy === 'hand' ? tr('notice.assembly.placedByHand')
                      : awaitsPlacing(fitting) ? tr('notice.assembly.placedByNobody') : '',
                ].filter(Boolean).join(' · ')}
                promptLabel={tr('notice.assembly.setHowRow')}
                load={() => Promise.resolve({ prompt: '' })}
                noPrompt
                run={{
                  label: tr(fromSet ? 'notice.assembly.placeFromSet'
                    : fromJoint ? 'notice.assembly.placeFromJoint'
                      : fromHole!.cover ? 'notice.assembly.placeCover' : 'notice.assembly.placeFromHole'),
                  onRun: () => void askMovement(),
                  busy: settingFromSet,
                  disabled: !!fromSet && state !== 'ready',
                }}
                // ONE ROW, like a call's prompt, reasoning and output: how the placing works
                // and what it came to are one thing to read, top to bottom, not two to choose
                // between.
                extra={[{
                  key: 'how',
                  icon: <FileText className="h-3.5 w-3.5" />,
                  label: tr('notice.assembly.setHowRow'),
                  // ON THE INSET A CALL'S WORDS SIT ON (CALL_INSET), as a prompt's do.
                  panel: (
                    <div className={`space-y-4 ${CALL_INSET}`}>
                      {tipSection(
                        tr('notice.assembly.setHowTitle'),
                        fromSet
                          ? tr('notice.assembly.setHow').replace('{run}', String(fromSet.of))
                          : fromJoint
                            ? tr('notice.assembly.jointHow')
                              .replace('{run}', String(fromJoint.of)).replace('{line}', fromJoint.line)
                            : fromHole!.h
                              ? tr('notice.assembly.holeHow').replace('{hole}', fromHole!.h.id)
                              : tr('notice.assembly.coverHow'),
                      )}
                      {tipSection(
                        tr('notice.assembly.setResultRow'),
                        JSON.stringify({
                          ...(fromHole
                            ? (fromHole.h ? { hole: fromHole.h.id } : {})
                            : { builtIn: `#${(fromSet ?? fromJoint!).of}` }),
                          ...(fromJoint ? { line: fromJoint.line } : {}),
                          seatMm: (fromSet ?? fitting).seat.map((v) => Math.round(v * 10) / 10),
                          axis: fitting.axis,
                          travelMm: fitting.travel,
                        }, null, 2),
                      )}
                    </div>
                  ),
                }]}
              />
            ) : null}
          </>,
          callSlot,
        )}

        {/* SAID WHILE IT HAPPENS, because the picture moves on its own when it lands: a part
            that slides four centimetres a second after the call came back, with nothing on
            screen saying why, reads as the viewer glitching. */}
        {seating && (
          <p className="flex items-center justify-center gap-2 text-center text-sm text-slate-500">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            {tr('notice.assembly.seating')}
          </p>
        )}

        {/* WHY NO RULE PLACES IT — the movement then has nobody's answer, and says what is missing. */}
        {noRule && noRule.length > 0 && (
          <div className="flex flex-col items-center gap-1">
            {noRule.map((why) => (
              <p key={why} className="max-w-2xl text-balance text-center text-sm text-amber-700">
                <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
                {why}
              </p>
            ))}
          </div>
        )}
        {setError && (
          <p className="text-balance text-center text-sm text-rose-700">
            <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
            {setError}
          </p>
        )}

        {/* EVERY SUB-STEP AT ONCE, on the strip the steps phase stands on — one press to any of
            them; the arrow keys still walk it. THE ONES THIS PHASE CANNOT STAND ON YET (no
            validated joint filled them) stay in sight, grey and not pressable. */}
        {allSlots.length > 0 && (
          <div ref={walk} className="w-full">
            {/* TWO STATES ON THE TILE ITSELF. FRAMED IN GREEN: signed off — by the demo's test
                above, so a repeat shows its leader's sign-off, and a frame because it still shows
                on the tile that is open and blue. WHITE: a movement nobody has signed off yet. */}
            <PartSelector
              parts={[
                ...allSlots.map((s) => {
                  const f = fittingFor(book, s);
                  const signed = f.validated && fittingChosen(f);
                  // A STEP TO A LINE: each one starts its own row of the strip.
                  return { key: s.key, name: s.label, row: String(s.step), disabled: !slots.includes(s), signed };
                }),
                // AND THE END ON A LINE OF ITS OWN: no step's, and not a gesture.
                // Greyed until the last sub-step is signed off, and its tooltip says so.
                { key: END_KEY, name: tr('notice.assembly.end'), row: END_KEY, disabled: !finished,
                  hint: finished ? undefined : tr('notice.assembly.endNotYet'), signed: !!signoffs.end },
                // AND EVERY SUB-STEP PLAYED THROUGH, last.
                { key: ALL_KEY, name: tr('notice.assembly.all'), row: END_KEY, signed: !!signoffs.all },
              ]}
              small
              activeKey={atEnd ? (atAll ? ALL_KEY : END_KEY) : slot?.key ?? ''}
              onSelect={(key) => {
                if (key !== END_KEY && key !== ALL_KEY) { goTo(slots.findIndex((s) => s.key === key)); return; }
                setPlaying(false);
                setAtAll(key === ALL_KEY);
                setAtEnd(true);
              }}
              widthBy="group"
            />
          </div>
        )}
        {!slots.length && <p className="text-center text-sm text-slate-400">{tr('notice.assembly.noValidatedJoint')}</p>}

      </div>

      {/* NO SELECTORS FOR THE TWO PARTS AND THE TOOL. There were three dropdowns here —
          which part moves, what it is fitted onto, which tool drives it — and every one of
          them asked again what is now answered upstream: the two parts by Phase 4's `role`
          (see roleSuggest, which fills the fitting from it), the tool by the sub-step's own
          tool list. What they offered was a correction, and a correction is worth a control
          only while the thing being corrected is a guess this page can improve on.
          It is not: this page sees one sub-step and Phase 4 saw the step. The chips above say
          which part is added and which receives it, in the same words the overview uses, and
          a wrong one is fixed where it was answered — by re-running that step. */}
      {/* THE MANUAL-WIDE COUNT OF MESHES USED TO BE WARNED ABOUT HERE — "this phase needs two
          of the manual's parts to have a 3D model" — and it was the wrong question asked in
          the wrong place. What stops this sub-step being drawn is whether ITS two parts have
          meshes, not how many the chair has: a manual with twenty modelled panels and no
          modelled hardware passed that count on every screen and drew nothing on half of
          them, in silence. The line below names the parts that are actually missing. */}

      {/* NOTHING TO DRAW, and it is no longer an instruction. It read "pick the part that
          moves, and the part it is fitted onto" — a thing to do, next to the three selectors
          that did it. There are no selectors: the parts come from Phase 4, so an unfilled
          fitting is not a job waiting for the reader, it is Phase 4 not having said which part
          moves here (a step analysed before the roles were asked for) or having named parts
          this document has no mesh for. Both are fixed somewhere else, and the line says so
          instead of asking for a click nothing offers. */}
      {atEnd && atAll ? (
        <div className="flex w-full flex-col gap-3">
          <AllClips docId={docId} />
          <SignoffToggle docId={docId} which="all" />
        </div>
      ) : atEnd && finished ? (
        // THE PRODUCT ASSEMBLED, as the published page ends on it: the same picture, the same
        // words, turned and zoomed like any other and with nothing to play.
        // ON A CARD OF ITS OWN, words and picture together: the page behind is the phase's dark
        // blue, and the two lines set straight on it were ink on ink.
        <div className={`${DESCRIBE_CARD} max-w-3xl items-center gap-2 p-4`}>
          <span className="text-sm font-bold uppercase tracking-wide text-slate-700">{tr('notice.public.doneTitle')}</span>
          <ClipPlayer
            clip={finished}
            label={tr('notice.public.doneTitle')}
            className="h-[28rem] w-full rounded-xl border border-slate-200 bg-white"
            autoPlay={false}
            still
            noBigView
            // ITS SENTENCE OVER THE PICTURE, in the box every sub-step's is in.
            caption={(
              <div className="pointer-events-none absolute inset-x-16 bottom-3 flex justify-center">
                <CaptionText text={tr('notice.public.doneText')} />
              </div>
            )}
          />
          <SignoffToggle docId={docId} which="end" />
        </div>
      ) : waiting || !fittingChosen(fitting) ? (
        <>
          {/* A repeat still waiting on its leader's sign-off says nothing: the reason is on the
              leader's page, not this one. */}
          {!waiting && (
            <p className="py-12 text-center text-sm text-slate-500">
              {undrawable.length > 0 && jobsLoaded
                // A PIECE AND A PART ARE MISSING FOR DIFFERENT REASONS, and are fixed in different
                // places: a part is reconstructed in the models phase; a piece is what an earlier
                // sub-step leaves when it is signed off, and no reconstruction makes one.
                ? [
                  undrawable.some((p) => (p.itemId ?? 0) < 0)
                    && `${tr('notice.assembly.noPiece')} ${undrawable.filter((p) => (p.itemId ?? 0) < 0).map((p) => p.label).join(', ')}. ${tr('notice.assembly.noPieceFix')}`,
                  undrawable.some((p) => !((p.itemId ?? 0) < 0))
                    && `${tr('notice.assembly.noMesh')} ${undrawable.filter((p) => !((p.itemId ?? 0) < 0)).map((p) => p.label).join(', ')}. ${tr('notice.assembly.noMeshFix')}`,
                ].filter(Boolean).join(' ')
                // WORKED OUT BY RULE AND NOT PLACED YET: the press that places it is the badge's.
                : fromSet || fromJoint || fromHole ? tr('notice.assembly.pressToPlace')
                : tr('notice.assembly.unfitted')}
            </p>
          )}
          {/* AND WHAT THE SUB-STEP IS, all the same — its kind, its sentence, its parts and
              its output are Phase 4's and need no picture to be read. */}
          <div className={DESCRIBE_CARD}>{description}</div>
        </>
      ) : (
        <>
          {/* THE PICTURE AND ITS TRANSPORT, ON ONE CARD, the sign-off included — as the joint
              view has it. No "enlarge" any more: nobody pressed it (Louis 2026-10-28). As wide as the
              page in the desktop's shape (see formFactor). */}
          <div className={formFactor === 'desktop' ? WIDE_PLAY_CARD : PLAY_CARD}>
          <div className="flex flex-col gap-4">
          <div className="relative">
          {/* THE PICTURE'S OWN BOX, the reader's shape (SHOT_ASPECT) and the reader's dress: the
              published player's ground, edge, light and caption, so what is framed here is what
              is published (Louis 2026-10-28). What is drawn over the picture is the reader's
              alone; every control of this phase hangs off the box's outer edges, in the white
              either side, and covers none of it. */}
          {/* In the desktop's, narrower than the card by the controls hanging off either side. */}
          <div className={`relative mx-auto ${SHOT_BOX[formFactor]} ${formFactor === 'desktop' ? 'max-w-[calc(100%-31rem)]' : 'max-w-full'}`}>
            {/* WHITE, IN BOTH THEMES, and not the page's own background — a 3D viewport is
                not page chrome. It is the same ground every other mesh in the app is judged
                against, which is worth more here than the mid grey it replaces: a fitting is
                read beside the part views in Phase 2, and a screw that changes colour between
                the two is a screw you have to think about twice. */}
            <div ref={host} className={`h-full w-full overflow-hidden ${READER_FRAME}`} />
            {/* HOW IT IS DRAWN, IN THE PICTURE IT IS ABOUT — the corner the parts list has
                left empty, level with the button that strips the marks off.
                It sat in a row of its own above the viewport, which cost a band of page for
                one control and put it further from the thing it changes than the thing is
                wide. It changes no byte of a fitting; it belongs on the picture, beside the
                other switch that only changes what is drawn. */}
            {/* FROM THE RIGHT, clear of the marks button in the corner — PartViewer's own
                arrangement. It was counted from the left, flush with the cards' left edge
                (13.75rem in), which held for two modes and broke at three: the toggle ran past
                the viewport and over the marks button.
                AND ON THE CARDS' OWN GROUND. It was transparent, which inside a panel is
                right and over a white viewport is not: the unselected half read as a white
                hole in the picture. */}
            {/* AND THE PICTURE WAITS TOO. Every pose in it is about to be replaced — where the
                fixed part stands, where the moving one comes from, how close the shot is — so
                the scene on screen is the previous answer, drawn at full strength, while the
                phase asks for another. Over the viewport rather than instead of it: the meshes
                are not being reloaded, and blanking them would cost the scene its context to
                say something a spinner says.

                Pointer events stay off it: orbiting and zooming go on working through a wait,
                which is worth more than the modality would be. */}
            {/* ONE SPINNER AND ONE SHORT LINE, whichever of the two waits it is: both used to draw
                their own, centred, and a sub-step loading AND being placed showed two stacked.
                What exactly it waits on is for the log (see waitingOn, sayWait), not the page. */}
            {(settling || state === 'loading') && (
              // WHOLE while a rule places it — see placingByRule, settling.
              <div className={`absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center ${
                settling ? `pointer-events-none ${READER_FRAME}` : ''}`}>
                <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                <p className="text-xs text-slate-500">
                  {tr(state === 'loading' ? 'notice.assembly.waitLoading' : 'notice.assembly.waitPlacing')}
                </p>
              </div>
            )}
            {/* THE VIEWER'S OWN CORNER, and everything that reads out of the viewport stacks
                in it. The angles are always there — a shot you cannot name is a shot you
                cannot come back to — and the picked part's numbers fall in under them when
                there is a picked part, rather than the two fighting over the same three
                pixels of margin. */}
            {/* EVERY CONTROL OF THE VIEW, IN ONE COLUMN IN THE WHITE TO THE RIGHT OF THE PICTURE
                (Louis 2026-10-28): the camera readout, how it is drawn, then the parts' cards under
                them, in the rest of the picture's height. */}
            <div className="pointer-events-none absolute left-full top-0 ml-3 flex h-full w-[14.25rem] flex-col items-start gap-2">
              {/* Only once the parts are up: these turn a camera, and there is no camera
                  until then — and a readout of an angle nothing is looking from is a number
                  about nothing.

                  Pointer events taken back: the stack is transparent so the viewport orbits
                  under it, and this is the one thing in it you press.

                  Centimetres out of the control and millimetres into the scene, which is the
                  one place those two units meet — the same edge the coordinates panel
                  converts at, for the same reason. */}
              {state === 'ready' && (
              <div className="pointer-events-auto">
                <ViewControls
                  sink={viewAngles}
                  onHome={() => view.current?.home()}
                  homeLabel={tr('notice.assembly.viewSquare')}
                  gizmoOn={gizmoOn}
                  onToggleGizmo={() => setGizmoOn((was) => !was)}
                  onTurn={(axis, degrees) => view.current?.turn(axis, degrees)}
                  // The six numbers in the corner, typed in: put the shot exactly here.
                  onLook={(shot) => view.current?.look(shot)}
                />
              </div>
              )}
              {/* HOW IT IS DRAWN, under the readout: it changes what is drawn and no byte of a
                  fitting, and over the picture it covered a third of its width. */}
              {state === 'ready' && (
                <div className="pointer-events-auto">
                  <SegmentedToggle
                    value={drawMode}
                    options={MODES}
                    onChange={(m) => { setDrawMode(m); view.current?.draw(m); }}
                    className="shadow-sm"
                    small
                  />
                </div>
              )}
            {/* WHAT IS IN THE PICTURE, down its right-hand side — one card per part the
                viewer has loaded. The eye takes one out of the way; the NAME selects it and
                unfolds its position and orientation underneath, in the same rectangle. Under
                the turn buttons, which are about the view as a whole; these are about what is
                in it, and now about where each of them is. */}
            {state === 'ready' && sceneParts && (
              // The rest of the column: the cards stand at their own right-3 top-3 inside it, so this is
              // their width and that margin, which lines their left edge up with the readout's.
              <div className="pointer-events-auto relative min-h-0 w-[13.75rem] flex-1">
              <ScenePartCards
                parts={sceneParts}
                hidden={hidden}
                onToggle={(instanceId) => setHidden((was) => (
                  was.includes(instanceId)
                    ? was.filter((id) => id !== instanceId)
                    : [...was, instanceId]
                ))}
                facesOff={facesOff}
                onToggleFaces={(instanceId) => setFacesOff((was) => (
                  was.includes(instanceId)
                    ? was.filter((id) => id !== instanceId)
                    : [...was, instanceId]
                ))}
                rulersOff={rulersOff}
                onToggleRuler={(instanceId) => setRulersOff((was) => (
                  was.includes(instanceId)
                    ? was.filter((id) => id !== instanceId)
                    : [...was, instanceId]
                ))}
                marksOn={marksOn}
                onToggleMarks={() => marks(!marksOn)}
                picked={picked?.instanceId ?? null}
                // Selected from the list, which is the same selection a click in the viewport makes.
                onPick={(instanceId) => setPicked(instanceId ? { instanceId } : null)}
                panel={coordsPanel}
              />
              </div>
            )}
            </div>
            {state === 'failed' && (
              <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-rose-600">
                {tr('notice.assembly.loadFailed')}
              </div>
            )}
            {/* BUILD THE SCENE AGAIN, without taking the page down with it. The mesh bench's
                own button — see PartViewer, where the argument for it is written — and this
                viewport needs it more: it holds two or three meshes rather than one, and the
                commonest way it comes up empty is not a dropped download but a WebGL context
                the browser took back to give one to something else. A page with a strip of
                thumbnails and a modal open has several, and the oldest is the one standing
                here.
                Reloading the whole phase to get the picture back costs the scroll position,
                the sub-step you were on and the shot you had framed. Always there and not
                only after a failure: a scene whose context was lost mid-life reports `ready`
                and draws nothing, which is exactly the state a failure banner does not cover.
                BOTTOM LEFT, the corner the bench uses, and for the same reason: the readout
                is top left and the cards are down the right. */}
            {/* THE SUB-STEP'S SENTENCE, the published tutorial's own (see Caption), its arrows
                moving between sub-steps as the strip above does. Taking no press but the arrows',
                so a drag that starts on it still turns the picture. NOT WHILE THE PICTURE IS LOADING
                OR BEING PLACED: a sentence and "1/3" over a spinner said a moment nothing was showing. */}
            {slotText && state === 'ready' && !settling && (
              <ReaderScale>
                <Caption
                  text={beat.text}
                  beat={beat}
                  // THE PUBLISHED PLAYER'S PLAY CONTROL, over the picture as there; the bar under
                  // it stays, for the frame-by-frame work done here.
                  lead={(
                    <RingAndSpeed
                      at={t}
                      playing={playing}
                      onToggle={toggle}
                      onSeek={(to) => { setPlaying(false); setT(to); }}
                      speed={speed}
                      onSpeed={() => setSpeed(nextSpeed)}
                    />
                  )}
                  onBack={() => goTo(at - 1)}
                  onNext={() => (at < slots.length - 1 ? goTo(at + 1) : setAtEnd(true))}
                  last={at >= slots.length - 1}
                />
              </ReaderScale>
            )}
            {/* AND IT PUTS THE PARTS BACK, where there is an answer to put them back to. The
                two belong on one press: what somebody means by "start this again" is the
                picture as it was first drawn, and a viewport rebuilt around a screw they have
                since dragged 4 cm sideways is only half of that.
                It says so on the button, and only where it is true — a sub-step nobody has
                asked about has nothing to restore, and the press is the plain redraw it has
                always been. */}
            <button
              type="button"
              // WHERE THE MOVEMENT IS WORKED OUT BY RULE, back to the rule's answer and never to an
              // older call's reply — which put its own camera angle over a part placed in its hole.
              // A set pair is only redrawn: its way in is measured on the scene being rebuilt.
              onClick={() => {
                setAttempt((n) => n + 1);
                if (fromJoint || fromHole) void askMovement();
              }}
              aria-label={tr(fromJoint || fromHole ? 'notice.assembly.reset' : 'notice.3d.reload')}
              title={tr(fromJoint || fromHole ? 'notice.assembly.reset' : 'notice.3d.reload')}
              // IN THE WHITE BESIDE THE PICTURE, level with its bottom edge.
              className="absolute bottom-0 right-full mr-3 rounded-lg border border-slate-300 bg-white/90 p-2 text-slate-500 shadow-sm transition-colors hover:border-blue-500 hover:text-blue-600"
            >
              <RotateCw className={`h-4 w-4 ${state === 'loading' ? 'animate-spin' : ''}`} />
            </button>
          </div>
          </div>

          {/* A part drawn at file scale is a part whose size is a proportion, and two
              proportions side by side make correct arithmetic look like a broken animation.
              Said here rather than left to be discovered. */}
          {prepWhy && (
            <p className="text-balance text-center text-xs text-amber-700">
              <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
              {tr('notice.assembly.prepNotPlaced').replace('{why}', prepWhy)}
            </p>
          )}
          {nextWhy && (
            <p className="text-balance text-center text-xs text-amber-700">
              <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
              {tr('notice.assembly.nextNotPlaced').replace('{why}', nextWhy)}
            </p>
          )}
          {lastWhy && (
            <p className="text-balance text-center text-xs text-amber-700">
              <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
              {tr('notice.assembly.lastNotPlaced').replace('{why}', lastWhy)}
            </p>
          )}
          {unscaled.length > 0 && (
            <p className="text-balance text-center text-xs text-amber-700">
              <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
              {tr('notice.assembly.unscaled').replace('{parts}', unscaled.join(', '))}
            </p>
          )}

          {/* A SIGN-OFF GIVEN ON ANOTHER GESTURE: Phase 4 changed this sub-step since (see
              gestureMoved). Said instead of the line below, whose marks would then blame the
              part the change dropped. */}
          {fitting.withdrawnFor === 'gesture' && !fitting.validated && (
            <p className="text-balance text-center text-xs text-amber-700">
              <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
              {tr('notice.assembly.gestureChanged')}
            </p>
          )}
          {/* A SIGN-OFF GIVEN ON ANOTHER MODEL of one of these parts. The movement may still be
              right; the tick no longer says so, because what was watched is not what is drawn. */}
          {changed.length > 0 && fitting.withdrawnFor !== 'gesture' && (
            <p className="text-balance text-center text-xs text-amber-700">
              <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
              {tr('notice.assembly.modelChanged').replace('{parts}', changedLabels.join(', '))}
            </p>
          )}

          {/* Beside the unscaled one and in the same words: the fact, and nothing about what
              to do. Both are about a thing the picture cannot show and neither is an error —
              a manual whose tools have no meshes still assembles. */}
          {toolGap && (
            <p className="text-balance text-center text-xs text-amber-700">
              <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
              {tr(toolGap.why === 'none'
                ? 'notice.assembly.toolNotChosen'
                : 'notice.assembly.toolNoMesh').replace('{tool}', toolGap.label)}
            </p>
          )}

          {/* NO BAR UNDER THE PICTURE: the reader's ring over it plays and scrubs (Louis 2026-10-28).
              What the bar said by refusing to play, said here. */}
          {overThread && (
            <p className="text-balance text-center text-xs text-amber-700">
              <AlertTriangle className="mr-2 inline h-4 w-4 align-[-3px]" />
              {tr('notice.assembly.overThread')}
            </p>
          )}
          </div>

          {/* THE SIGN-OFF, under the thing it signs off. Unvalidated until somebody has
              watched this animation and said otherwise — never inferred from the fitting
              being complete, because a fitting with the right two parts and a wrong axis is
              complete and wrong, and only watching it tells the two apart.

              It is also what Phase 5's bar counts and what Phase 6 draws instead of an empty
              frame, so it is the one control here that changes something outside this
              phase. Any later edit to the movement clears it — see setFitting.

              ONE TICK PER GESTURE, and it is on the sub-step that describes it. Six identical
              washer-onto-screw sub-steps are one thing to watch and one thing to judge; six
              toggles would be five chances to say that the same animation is both right and
              wrong. So the repeats have none, and this one signs off all of them.

              NOT IN THE DEMO. It is a judgement — somebody watched this and said it was
              right — and the sign-off it records is not this copy's to give: the run on
              screen was validated on the real app, by the person who watched it there.
              It is also the one control in this phase that changes something outside it,
              and it bakes a mesh on the way (a call the demo would refuse anyway). What
              it decided is still visible where it lands: Phase 5's bar counts the
              sign-offs, and this phase draws the baked movement rather than an empty
              frame. */}
          {/* A REPEAT HAS NO SIGN-OFF OF ITS OWN, and says whose it is: the toggle simply missing read
              as a fault. The leader's label goes there. */}
          {leader && !IS_DEMO && (() => {
            const at = slots.findIndex((s) => s.key === leader);
            const [before, after] = tr('notice.assembly.sameAs').split('{label}');
            return at < 0 ? null : (
              <p className="text-center text-sm text-slate-500">
                {before}
                <button type="button" onClick={() => goTo(at)} className="font-semibold text-notice-blue underline-offset-2 hover:underline">
                  {slots[at].label}
                </button>
                {after}
              </p>
            );
          })()}
          {!leader && !IS_DEMO && (
          <div className="flex justify-center">
            <SegmentedToggle
              value={fitting.validated ? 'yes' : 'no'}
              options={[
                { value: 'no', label: tr('notice.assembly.unvalidated') },
                { value: 'yes', label: tr('notice.assembly.validated') },
              ]}
              onChange={(v) => {
                const said = v === 'yes';
                write(said
                  ? { validated: true, signedOn: signedNow, signedFor: slot?.gesture ?? undefined, withdrawnFor: undefined }
                  // Unticked by hand: whoever did it knows why, and the marks would only make the line
                  // under the viewer say something about a sign-off nobody is relying on.
                  : { validated: false, signedOn: undefined, signedFor: undefined, withdrawnFor: undefined });
                // AND THE THING IT SIGNED OFF, KEPT AS A MESH. A validated movement has a
                // final position, and that position is an OBJECT — the sub-step's own output
                // chip, "101 – Prepared screw". Every later sub-step is about it rather than
                // about the two parts it was made from, and until it had an id it could only
                // be seen by assembling those parts again.
                //
                // Baked from the finished position, whatever frame is on screen: the sign-off is
                // about where the parts end up, and t=0.4 would save them on their way there.
                //
                // A failure is a failure of the SAVE and not of the sign-off, which is a click
                // somebody made and stands whatever happened afterwards.
                //
                // AND PRESSING "VALIDATED" WHILE ALREADY VALIDATED BAKES IT AGAIN, which is the
                // only way one is ever asked for: a mesh is a picture of a moment and the moment
                // moves — a part nudged two millimetres, a mesh regenerated, a sub-step signed
                // off before any of this existed. Every save is a new row and the newest wins
                // (see the route), so what Phase 2 shows is what was last on screen here.
                if (said) void keepOutput();
              }}
            />
          </div>
          )}
          {/* THE VIEWS EACH SHOT WEIGHED, under the sign-off: what the sign-off is about comes first.
              ON A CARD OF THEIR OWN, FOLDED until asked for (Louis 2026-10-28): two dozen pictures
              were a page of scrolling under every sub-step. Folded, none is taken, so the camera is
              never borrowed to draw a view nobody is looking at. */}
          {!leader && (
            <div className={`${DESCRIBE_CARD} mt-3`}>
              <button
                type="button"
                onClick={() => setViewsOpen((was) => !was)}
                aria-expanded={viewsOpen}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:text-slate-900"
              >
                {tr('notice.assembly.candidates.title')}
                {viewsOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {viewsOpen && (
              <div className="border-t border-slate-200 px-3 pb-3">
            <ShotCandidates
              key={slot?.key}
              rows={candidateRows()}
              scene={view.current}
              asm={asm}
              step={STEP}
              ready={state === 'ready' && !settling && !playing}
              log={(line) => sayWait(slot?.key ?? '', line)}
              tried={viewTried}
              onTry={(on, row, look) => {
                // AT THE ROW'S MOMENT, from that view: what the picture showed, now in the viewer, and played from there.
                setViewTried(on);
                triedRef.current = on;
                setPlaying(false);
                taken.current = false;
                setT(row.at);
                showAt(row.at);
                view.current?.look(look);
              }}
              hold={() => {
                const was = liveView.current;
                return () => {
                  showAt(t);
                  // The view as reported is centred in CENTIMETRES (the readout's); `look` aims in
                  // millimetres. Handed back as read, the camera came back aimed ten times nearer
                  // the origin.
                  if (was) view.current?.look({ ...was, centre: was.centre.map((c) => c * 10) as [number, number, number] });
                };
              }}
            />
              </div>
              )}
            </div>
          )}
          </div>

          {/* AND WHETHER THE TWO PARTS ACTUALLY CLEAR EACH OTHER, under the sign-off it
              informs. It is the one thing on this page nobody can answer by looking: a wrong
              axis brings the moving part through the fixed one for a few frames in the
              middle of a movement that otherwise plays perfectly. See ClashBar.

              It goes with the sign-off it informs: a repeat is not being judged here, and a
              clearance reading under an animation nobody on this page may change is a
              measurement with nothing to do about it. */}
          {!leader && (
          <ClashBar
            profile={clash.profile}
            busy={clash.busy}
            t={t}
            onSeek={(to) => { setPlaying(false); setT(to); }}
            marked={clashMarked}
            onMarked={setClashMarked}
          />
          )}

          {/* THE SUB-STEP CARD AND ITS NUMBERS, LAST — and moved inside this branch to
              get there. What kind of gesture this is, what it says, what it takes and
              what it leaves are the description; the movement above is the thing being
              described, and the clearance under it is the verdict on that movement. Read
              in that order the page is picture, verdict, particulars rather than a form
              you fill before you are shown anything.

              The "nothing to draw" branch draws the description alone, on the same card
              (see `description`). */}
          {/* ONE CARD FOR THE DESCRIPTION AND THE NUMBERS. They were two, stacked with a gap
              between them, which read as two answers about two different things — and they
              are one: what this gesture is, and where the parts are while it happens. The
              chrome is here so there is a single border round the pair, and `divide-y` draws
              the line between them only when both are there (a repeat has no readout).

              Full width, with the four columns centred inside it (see panelBox). */}
          {/* AS WIDE AS THE COLUMNS where they are all it holds (a repeat has no numbers): full
              width, they sat in a band of white either side of them. */}
          <div className={`${leader ? DESCRIBE_CARD.replace('w-full', 'w-fit max-w-full px-3') : DESCRIBE_CARD} divide-y divide-slate-200`}>
          {description}

          {/* HOW IT ARRIVES. Every number is in the target's frame and in millimetres, so
              the moving part starts at seat + axis × travel with nothing converted between
              the field and the screen.

              Boxed together because they are ONE answer given in eight fields — change any
              of them and the same movement changes — where the selectors above are three
              separate choices and the picture is what they come out as. Loose on the page
              they read as eight unrelated settings.

              OVER THE PICTURE. They sat under it, on the argument that the animation is what
              this phase is FOR and the numbers are what you come back to once you have seen it
              go wrong. That was written when the bar was two grids of coordinates and a camera
              block — the camera is gone, and what is left is one line of tables per part. The
              order that reads now is the sentence's own: here is where each part is, and here
              is the thing moving between those numbers. */}
          {/* AND EVERY NUMBER IN IT IS THE LEADER'S. A repeat has no movement of its own to
              read out and nothing here it could type over — the tables are the one control on
              this page that looks like a readout, which is exactly why they cannot be left
              standing on a page where they answer nothing. */}
          {!leader && (
          <div className="flex flex-col gap-4 px-4 py-4">
            {/* THE CAMERA'S OWN BAR IS GONE, and the rule that separated it from this one.
                It read where the shot starts, where it ends and what changes between — three
                columns of a movement that cannot happen: every sub-step is watched from
                FIXED_CAM, square on the origin, the same at both ends, and has been since the
                model stopped being asked for a shot. Six boxes and two buttons for a value
                nothing writes and nothing reads is not a control, it is a claim that the
                camera is answerable here. */}
            {/* HOW CLOSE IT IS WATCHED FROM, first, because it is a fact about the whole
                picture rather than about any part in it — and because it is the one number
                here that explains why the others look the size they do. Read-only: the shot
                is dragged and wheeled in the viewport, and a second way to set it would be a
                second place for it to disagree.

                ×1 IS THE SHOT THAT FITS EVERYTHING. Above it the camera stands closer; a
                rule's shot is framed on the action (see movementShot) rather than on a room
                sized by whichever part is furthest from the origin. */}
            <p className="flex items-center justify-center gap-2 text-center text-[11px] uppercase tracking-wide text-slate-500">
              {tr('notice.assembly.camZoom')}
              <span className="font-mono text-xs font-bold tabular-nums text-slate-700">
                ×{(fitting.camEndZoom ?? 1).toFixed(2)}
              </span>
            </p>

            {/* AND WHICH SIDE IT IS WATCHED FROM, which the call answers now. Beside the
                distance because they are the two halves of one fact — where the camera is —
                and above the parts for the same reason the distance is: it is about the
                picture rather than about anything in it.

                In the corner's own three colours and the corner's own order, since the corner
                is where the same three numbers are read live and typed over. */}
            <p className="flex items-center justify-center gap-2 text-center text-[11px] uppercase tracking-wide text-slate-500">
              {tr('notice.assembly.camAngle')}
              <span className="flex items-center gap-1.5 font-mono text-xs font-bold tabular-nums">
                {AXES.map(({ axis, colour }, i) => (
                  <span key={axis} style={{ color: colour }}>
                    {Math.round(fitting.camEndRot?.[i] ?? 0)}°
                  </span>
                ))}
              </span>
            </p>

            {/* AND ONE ROW PER PART THAT DOES NOT MOVE. A fitting draws two or three parts and
                the bar spoke for one of them: the piece being fitted onto had its coordinates
                on a card nobody opens, and the tool had none anywhere. They are readings of
                the same six numbers in the same frame, so they are drawn in the same table —
                one column, because a part that does not move has one pose, and a start and a
                final for it would be the same numbers twice.

                LIVE, off the scene rather than off the fitting: it is the pose the viewer is
                drawing, which is the pose a reader is checking. Read-only here — a still part
                is placed by dragging it or by its own panel, and a third way in would be a
                third place for the answer to differ. */}
            {still.map((p) => (
              <MovementSummary key={p.id} part={p.label} end={p.pose} still />
            ))}

            {/* THE MOVEMENT BETWEEN ITS TWO ENDS — see MovementSummary. The controls that
                define it go in the middle column, because they are the only part of this a
                person can edit and the columns either side are what editing them does. */}
            <MovementSummary
              start={startPose}
              end={endPose}
              onStart={writeStart}
              onEnd={writeFinal}
              // WHOSE MOVEMENT IT IS. The moving part, which is the only one of the two that
              // travels — the fixed part's own place is answered on its card. Its label
              // already carries the letter the diagram calls it by; see `choices`.
              part={fitting.movingId != null ? byItem.get(fitting.movingId)?.label : undefined}
              // AND WHERE IT IS RIGHT NOW, in the middle, live. Which way in and how far were
              // controls there; they are readings of the two tables either side now, and what
              // the column is worth saying is the one thing those two cannot: where the part
              // has got to.
              now={nowPose}
              moment={t}
            />

            {/* HOW IT GOES IN, AND WHETHER IT THREADS — and a placing fastens nothing, so
                there is nothing to thread: the pitch and the depth describe a fastener biting
                into something, and a rail held against a frame bites nothing. Left on screen
                they were fields that changed nothing.

                THIS IS THE LAST THING THE FAMILY DECIDES. It used to decide how many parts
                were drawn, which frame the seat was written in and whether there was a tool —
                all of it inherited from the kind that moved a single part and joined it to
                nothing, which is no longer one of the four. What is left is what the two
                words have always actually meant: is anything fastened here. */}
            {!placing && (
            <div className="flex flex-col items-center gap-1.5">
              {/* WHAT THERE IS TO DIVIDE UP, said above the field that divides it. Without it
                  the thread is a number typed against nothing: the distance it has to fit
                  inside is the one number about this movement that is nowhere on this row. */}
              {fitting.drive === 'turned' && (
                <p className={`text-[11px] uppercase tracking-wide ${
                  overThread ? 'font-semibold text-red-600' : 'text-slate-500'
                }`}
                >
                  {tr('notice.assembly.travelled').replace('{mm}', fitting.travel.toFixed(1))}
                </p>
              )}
              <div className="flex flex-wrap items-end justify-center gap-4">
              {/* WHERE THE PART ENDS UP IS NOT ASKED HERE ANY MORE.
                  It was three boxes — FINAL X, Y, Z — writing the same `seat` the part's own
                  card already writes, and the card's are the better pair of hands: they are
                  typed in the assembly's frame and in centimetres, which is the frame the
                  gizmo draws and the units every other readout on this page uses, and they
                  run the transport to the end before committing, so what was typed is what is
                  on screen. These wrote at whatever frame the scrubber happened to be on.
                  One answer, one place to type it, and dragging the part for the rest. */}
              {/* HOW IT GOES IN, and the two numbers only where they answer something.
                  Most sub-steps bring a part straight in — slid on, pushed home, offered up —
                  and a pitch and a thread depth on those are a form asking a question the
                  movement does not have. Turned is the case that cannot be keyframed: one
                  turn advances the part by exactly its pitch, so the angle is computed from
                  the distance left rather than animated beside it.
                  THE SWITCH IS ALWAYS THERE. It carries the analysis's answer and the
                  analysis can be wrong, so saying "this one winds in" is one press rather
                  than a re-run. Turning it back to straight takes the two numbers with it,
                  which is what keeps a fitting from saying straight and playing a helix. */}
              <label className="flex flex-col items-center gap-1">
                <span className="text-[11px] uppercase tracking-wide text-slate-500">
                  {tr('notice.assembly.drive')}
                </span>
                <SegmentedToggle
                  value={fitting.drive}
                  options={[
                    { value: 'straight' as Drive, label: tr('notice.assembly.driveStraight') },
                    { value: 'turned' as Drive, label: tr('notice.assembly.driveTurned') },
                  ]}
                  onChange={(v) => set(v === 'straight'
                    ? { drive: v, pitch: 0, depth: 0 }
                    : { drive: v })}
                />
              </label>
              {fitting.drive === 'turned' && (
              <>
              <MmField
                label={tr('notice.assembly.pitch')}
                value={fitting.pitch}
                onChange={(v) => set({ pitch: Math.max(0, v) })}
                step={0.25}
              />
              <MmField
                label={tr('notice.assembly.depth')}
                value={fitting.depth}
                onChange={(v) => set({ depth: Math.max(0, v) })}
                // AS DEEP AS THE PART TRAVELS AND NO DEEPER. The thread is a slice of the
                // travel, so the travel is the whole of it.
                max={fitting.travel}
                // AND THE WARNING STAYS, for the case the field cannot catch: the travel is
                // not typed here. Move either end of the movement and a thread that was
                // legal becomes too deep without this box being touched.
                wrong={overThread}
              />
              </>
              )}
              </div>
            </div>
            )}
          </div>
          )}
          </div>

        </>
      )}
    </div>
  );
}
